/**
 * AGM Deadline Engine — Pure Computation Module (ESM)
 *
 * Single source of truth for all compliance deadline calculations.
 * Zero dependencies. Works in browser and Node (CJS mirror in backend).
 * Every function is pure: same input -> same output. No side effects.
 *
 * Kept in sync with backend/new-ui-generation/shared/agmDeadlineEngine.js.
 * If you change logic here, update the CJS mirror too.
 */

// ─── Date Helpers ─────────────────────────────────────────────

function toDate(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

function addDays(date, days) {
  const d = toDate(date);
  if (!d) return null;
  const result = new Date(d);
  result.setDate(result.getDate() + days);
  return result;
}

function addMonths(date, months) {
  const d = toDate(date);
  if (!d) return null;
  const result = new Date(d);
  // Clamp to last day of target month to avoid JS overflow (e.g. Mar 31 + 6mo → Oct 1).
  const originalDay = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(originalDay, lastDay));
  return result;
}

function startOfDay(date) {
  const d = toDate(date);
  if (!d) return null;
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

function diffDays(a, b) {
  const da = startOfDay(a);
  const db = startOfDay(b);
  if (!da || !db) return null;
  return Math.round((da - db) / 86400000);
}

function formatDateEN(d) {
  const date = toDate(d);
  if (!date) return '\u2014';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Entity Type Normalizer ──────────────────────────────────

const ENTITY_ALIAS = {
  'pvtlimited': 'pvtlimited',
  'pvt ltd': 'pvtlimited',
  'private limited': 'pvtlimited',
  'private': 'pvtlimited',
  'public': 'public',
  'public limited': 'public',
  'public ltd': 'public',
  'opc': 'opc',
  'one person': 'opc',
  'one person company': 'opc',
  'sec8': 'sec8',
  'sec 8': 'sec8',
  'section 8': 'sec8',
  'ngo': 'sec8',
  'section 8 / ngo': 'sec8',
  'llp': 'llp',
  'limited liability partnership': 'llp',
  'foreign company': 'pvtlimited',
  'nidhi company': 'pvtlimited',
  'producer company': 'pvtlimited',
  'government company': 'public',
};

function normalizeEntityType(type) {
  if (!type) return 'pvtlimited';
  const key = String(type).trim().toLowerCase();
  return ENTITY_ALIAS[key] || 'pvtlimited';
}

// ─── FY End Parser ───────────────────────────────────────────

const MONTH_NAMES = {
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
};

/**
 * Parse a FY-end descriptor into a concrete Date for a given reference year.
 * Accepts: "03-31", "31 March", "March 31", Date object.
 * Returns the most recent FY end date that is <= referenceDate (or today).
 */
function parseFyEnd(fyEnd, referenceDate) {
  const ref = toDate(referenceDate) || new Date();
  let month = 2, day = 31; // default: 31 March

  if (fyEnd instanceof Date && !isNaN(fyEnd.getTime())) {
    month = fyEnd.getMonth();
    day = fyEnd.getDate();
  } else if (typeof fyEnd === 'string' && fyEnd.trim()) {
    const str = fyEnd.trim();

    // "MM-DD" format
    if (/^\d{2}-\d{2}$/.test(str)) {
      const [m, d] = str.split('-').map(Number);
      month = m - 1;
      day = d;
    }
    // "31 March" or "March 31" format
    else {
      const parts = str.toLowerCase().split(/[\s,]+/).filter(Boolean);
      for (const part of parts) {
        if (MONTH_NAMES[part] !== undefined) month = MONTH_NAMES[part];
        else if (/^\d{1,2}$/.test(part)) day = parseInt(part, 10);
      }
    }
  }

  // Find the most recent FY end date <= ref
  let fyEndDate = new Date(ref.getFullYear(), month, day);
  if (fyEndDate > ref) fyEndDate = new Date(ref.getFullYear() - 1, month, day);

  return fyEndDate;
}

/**
 * Build a human-readable FY label like "FY 2025-26".
 */
function fyLabel(fyEndDate) {
  const d = toDate(fyEndDate);
  if (!d) return '';
  const endYear = d.getFullYear();
  // For March ending (or Jan/Feb), the FY starts the previous calendar year
  if (d.getMonth() <= 2) {
    return `FY ${endYear - 1}-${String(endYear).slice(2)}`;
  }
  // For other endings, FY starts same year and ends next
  return `FY ${endYear}-${String(endYear + 1).slice(2)}`;
}

// ─── Filed Tracking ──────────────────────────────────────────

/**
 * Compact FY key for the filed-tracking map, e.g. "2025-26".
 * Mongoose Map keys can't contain a dot, so the composite key below is
 * `formKey:fyKey` — both halves are dot-free by construction.
 */
function fyKey(fyEndDate) {
  return fyLabel(fyEndDate).replace(/^FY\s*/, '');
}

// AOC-4 and AOC-4 XBRL are the same statutory obligation filed on two different
// forms, so they share ONE filed-tracking slot. Without this, switching a company
// to XBRL would orphan an already-recorded `aoc4:2024-25` flag and resurrect a
// settled cycle as overdue.
const FILING_KEY_ALIAS = { aoc4xbrl: 'aoc4' };

/** Canonical filed-tracking form key (collapses form variants — see the alias map). */
function filingFormKey(formKey) {
  return FILING_KEY_ALIAS[formKey] || formKey;
}

/** Storage key for one form's "Mark as Filed" flag in a given FY cycle. */
function filingKey(formKey, fyEndOrKey) {
  const cycle = typeof fyEndOrKey === 'string' ? fyEndOrKey : fyKey(fyEndOrKey);
  return `${filingFormKey(formKey)}:${cycle}`;
}

/**
 * Normalize a stored filings map into
 * `{ [key]: { filed, filedAt, source, asOf, srn, filedOn } }`.
 *
 * Accepts a plain object (API JSON), a Mongoose Map and `true`/`false`
 * shorthand. Unknown key = NOT filed.
 *
 * Explicitly un-filed records are KEPT (with `filed: false`) rather than
 * dropped: "the user says this is not filed" is itself an assertion, and it has
 * to survive so a later registry-derived flag cannot silently overwrite it.
 * Callers must therefore test `entry.filed`, never the entry's presence.
 *
 * `srn` / `filedOn` are the OPTIONAL evidence a user may attach when marking a
 * form filed — the MCA challan's SRN and the date the form actually went in.
 * Both default empty, because evidence is optional by design: a bare "filed"
 * assertion is still a valid assertion, and requiring paperwork would make the
 * quick path slow enough that people stop recording filings at all. They are
 * distinct from `filedAt`, which is when the flag was RECORDED here.
 */
/**
 * Registry-derived provenance labels for a filed record.
 *
 * Two labels, not one, because they are two different sources with different
 * trust profiles and they must be captioned differently:
 *
 *   'mca-open-data'  the data.gov.in MCA master resource / the MCA21
 *                    `AllMDSData.XLSX` export. Bulk, published on a lag, and
 *                    narrow (no filing history) — everything written today.
 *   'mca-portal'     a V3 company-master page read directly. Authoritative and
 *                    current. RESERVED: nothing writes it yet, but validation
 *                    and captions accept it so the scraper can land without a
 *                    lock-step change here and in the model.
 *
 * Anything else — including an absent source — is 'manual'.
 */
const REGISTRY_FILING_SOURCES = ['mca-open-data', 'mca-portal'];

const REGISTRY_SOURCE_CAPTIONS = {
  'mca-open-data': 'MCA master data',
  'mca-portal': 'MCA portal',
};

/**
 * Canonicalise a stored `source` value.
 *
 * `'mca'` is accepted as a READ-ONLY legacy alias for 'mca-open-data': records
 * written before the split carry it, and the open-data path is the only thing
 * that could have produced them (the portal reader does not exist). Normalising
 * on read rather than migrating means an old document renders correctly with no
 * backfill, and a half-migrated collection cannot produce two captions for the
 * same fact.
 */
function normalizeFilingSource(value) {
  const v = String(value == null ? '' : value).trim();
  if (v === 'mca') return 'mca-open-data';
  return REGISTRY_FILING_SOURCES.includes(v) ? v : 'manual';
}

/** Is this source a registry fetch (rather than a human assertion)? */
function isRegistryFilingSource(value) {
  return REGISTRY_FILING_SOURCES.includes(normalizeFilingSource(value));
}

function normalizeFilings(filings) {
  if (!filings || typeof filings !== 'object') return {};
  const src = typeof filings.entries === 'function' && !Array.isArray(filings)
    ? Object.fromEntries(filings.entries())
    : filings;
  const out = {};
  for (const [key, value] of Object.entries(src)) {
    if (value === null || value === undefined) continue;
    if (value === true || value === false) {
      out[key] = { filed: value, filedAt: null, source: 'manual', asOf: null, srn: '', filedOn: null };
      continue;
    }
    if (typeof value !== 'object') continue;
    out[key] = {
      filed: value.filed !== false,
      filedAt: toDate(value.filedAt),
      source: normalizeFilingSource(value.source),
      asOf: toDate(value.asOf),
      srn: String(value.srn || '').trim().toUpperCase(),
      filedOn: toDate(value.filedOn),
    };
  }
  return out;
}

/**
 * Does this filed record carry evidence (an SRN or a real filing date)?
 *
 * The UI branches on this rather than on `source`: a row WITH evidence shows its
 * basis caption, a row without keeps the plain "Filed" pill. That keeps the
 * quick path visually quiet while making a documented filing visibly documented.
 */
function hasFilingEvidence(entry) {
  if (!entry || !entry.filed) return false;
  return !!(String(entry.srn || '').trim() || toDate(entry.filedOn));
}

/**
 * Overlay manual filed records on registry-derived ones.
 *
 * A MANUAL record always wins, in either polarity. A user who marked a form
 * filed — or explicitly marked it NOT filed — must never be silently reversed
 * by a later MCA fetch. `overridesMca` marks the rows where that happened so the
 * UI can offer "reset to the registry value".
 */
function mergeFilings(manualFilings, mcaFilings) {
  const out = {};
  for (const [key, value] of Object.entries(mcaFilings || {})) out[key] = { ...value, overridesMca: false };
  for (const [key, value] of Object.entries(manualFilings || {})) {
    out[key] = { ...value, overridesMca: Object.prototype.hasOwnProperty.call(mcaFilings || {}, key) };
  }
  return out;
}

/**
 * Human basis line for a filed record — the freshness ladder for registry
 * facts. A registry-derived flag NEVER states bare "Filed": it states where it
 * came from and, when known, the as-of date of that fetch, so a stale fetch is
 * never read as current truth.
 *
 * A manual record with evidence (SRN and/or a real filing date) leads with that
 * evidence, because an SRN is checkable against MCA and therefore outranks "a
 * user said so" as a basis. A manual record WITHOUT evidence keeps its original
 * wording ("Marked filed on …") — it is an assertion, and it says so.
 */
function filedBasisLabel(entry) {
  if (!entry) return '';
  if (isRegistryFilingSource(entry.source)) {
    // Names WHICH registry, not a generic "MCA data": open-data is a bulk export
    // on a publication lag, the portal is the live record, and a reader deciding
    // whether to trust the row needs to know which one they are looking at.
    const what = REGISTRY_SOURCE_CAPTIONS[normalizeFilingSource(entry.source)];
    const when = entry.asOf ? formatDateEN(entry.asOf) : '';
    const stamp = when ? `per ${what}, fetched ${when}` : `per ${what} (fetch date unknown)`;
    return entry.filed ? `Filed — ${stamp}` : `Not filed — ${stamp}`;
  }
  if (!entry.filed) return 'Marked not filed';
  const srn = String(entry.srn || '').trim().toUpperCase();
  const filedOn = toDate(entry.filedOn);
  if (srn && filedOn) return `Filed — SRN ${srn}, ${formatDateEN(filedOn)}`;
  if (srn) return `Filed — SRN ${srn}`;
  if (filedOn) return `Filed on ${formatDateEN(filedOn)}`;
  const when = entry.filedAt ? formatDateEN(entry.filedAt) : '';
  return when ? `Marked filed on ${when}` : 'Marked filed';
}

/**
 * Filed flags the MCA registry itself implies — no manual marking needed.
 *
 * REALITY CHECK, because this is deliberately narrow: the live MCA master
 * dataset (data.gov.in resource 4dbe5667-…) publishes NO filing history, no SRN
 * list and no per-form last-filed dates. `companyController.getCompanyMcaDiff`
 * already states as much (`notPublishedByMca` includes `lastAgm`). The only
 * filing-derived facts reachable today are `Date of Balance Sheet` and
 * `Date of last AGM`, which arrive via the MCA21 `AllMDSData.XLSX` import — and
 * of those only the balance-sheet date maps to a specific form:
 *
 *   a balance-sheet date on record for FY X ⇒ AOC-4 for FY X was filed.
 *
 * `Date of last AGM` is NOT used: it proves the AGM was held (the engine
 * already consumes `lastAgm` for that), not that MGT-7 or ADT-1 were filed.
 *
 * Gated on `balanceSheetDateSource` — the PER-FIELD provenance of the one fact
 * this derivation rests on — and NOT on the record-wide `mcaDataAsOf` stamp.
 *
 * The coarse stamp was the wrong gate, and wrong in the unsafe direction. The
 * live open-data resource does not publish a balance-sheet date at all (see the
 * `balanceSheetDate` entry in MCA_APPLY_FIELDS, which is whitelisted but
 * skip-absent for exactly this reason). So a perfectly successful live refresh
 * sets `mcaDataAsOf` while `balanceSheetDate` remains whatever a human typed —
 * opening the gate and captioning a hand-typed date "per MCA master data". Only
 * the field's own source line can carry that claim.
 *
 * Direction of error is safe by construction: we only ever assert FILED. A
 * stale fetch can miss a newer filing (leaving a row overdue, which the manual
 * toggle fixes) but can never invent a filing that did not happen.
 */
function deriveRegistryFilings(company) {
  const source = normalizeFilingSource(company?.balanceSheetDateSource);
  if (!isRegistryFilingSource(source)) return {};
  const balanceSheetDate = toDate(company?.balanceSheetDate);
  if (!balanceSheetDate) return {};
  // Prefer the field's own as-of; fall back to the record-wide stamp so a row
  // written before `balanceSheetDateAsOf` existed still shows a dated caption
  // rather than the bleak "(fetch date unknown)".
  const asOf = toDate(company?.balanceSheetDateAsOf) || toDate(company?.mcaDataAsOf);
  return {
    [filingKey('aoc4', fyKey(balanceSheetDate))]: {
      filed: true,
      filedAt: null,
      source,
      asOf,
    },
  };
}

// ─── LLP Late-Fee Slabs (LLP (Amendment) Rules, 2022) ────────

// Normal filing fee for LLP annual forms (Form 8 / Form 11) by contribution
// slab — Annexure 'A' of the LLP Rules 2009 as substituted by the LLP
// (Amendment) Rules 2022 (notified 11 Feb 2022, effective 1 Apr 2022).
const LLP_NORMAL_FEE_SLABS = [
  { maxContribution: 100000, fee: 50 },
  { maxContribution: 500000, fee: 100 },
  { maxContribution: 1000000, fee: 150 },
  { maxContribution: 2500000, fee: 200 },
  { maxContribution: 10000000, fee: 400 },
  { maxContribution: Infinity, fee: 600 },
];

// Assumed normal fee when contribution is unknown (₹1–5 lakh slab).
const LLP_DEFAULT_NORMAL_FEE = 100;

// Additional (late) fee = multiplier × normal filing fee, by delay band.
// Small LLP = contribution ≤ ₹25 lakh AND turnover ≤ ₹40 lakh (Sec 2(1)(ta),
// LLP Act). Small LLPs pay half the multiplier of other LLPs in every band.
const LLP_ADDITIONAL_FEE_BANDS = [
  { maxDays: 15, small: 1, other: 1 },
  { maxDays: 30, small: 2, other: 4 },
  { maxDays: 60, small: 4, other: 8 },
  { maxDays: 90, small: 6, other: 12 },
  { maxDays: 180, small: 10, other: 20 },
  { maxDays: 360, small: 15, other: 30 },
];

// Beyond 360 days, Form 8 / Form 11 specifically: 15× / 30× the normal fee
// plus ₹10 / ₹20 per day for every day beyond the 360th.
const LLP_BEYOND_360 = {
  small: { multiplier: 15, perDay: 10 },
  other: { multiplier: 30, perDay: 20 },
};

function llpNormalFee(contributionInr) {
  const c = Number(contributionInr);
  if (!(c > 0)) return LLP_DEFAULT_NORMAL_FEE;
  for (const slab of LLP_NORMAL_FEE_SLABS) {
    if (c <= slab.maxContribution) return slab.fee;
  }
  return LLP_NORMAL_FEE_SLABS[LLP_NORMAL_FEE_SLABS.length - 1].fee;
}

// smallLlp defaults to false (the conservative, higher rate) — only treat an
// LLP as small when both thresholds are positively known to be met.
function estimateLlpSlabPenalty(daysOverdue, { smallLlp = false, contributionInr = null } = {}) {
  if (daysOverdue <= 0) return 0;
  const fee = llpNormalFee(contributionInr);
  const rate = smallLlp ? 'small' : 'other';
  const band = LLP_ADDITIONAL_FEE_BANDS.find((b) => daysOverdue <= b.maxDays);
  if (band) return band[rate] * fee;
  const beyond = LLP_BEYOND_360[rate];
  return beyond.multiplier * fee + beyond.perDay * (daysOverdue - 360);
}

// ─── Companies Act Late-Fee Slabs ────────────────────────────
//
// Companies (Registration Offices and Fees) Rules, 2014 — Rule 12 read with the
// Annexure "Table of Fees". The additional-fee table was substituted by the
// Companies (Registration Offices and Fees) Second Amendment Rules, 2018
// (G.S.R. 435(E), w.e.f. 7 May 2018) and re-substituted in the same shape by the
// Amendment Rules, 2022 (w.e.f. 1 Jul 2022). Bands and multipliers below are the
// text common to both.
//
// This is NOT a per-day regime. The additional fee is a MULTIPLE of the normal
// filing fee, stepped by delay band, and it stops growing at 12×. Modelling it
// as ₹300/day (what the ADT-1 entry used to do) overstates a two-week slip by
// more than an order of magnitude and keeps climbing forever.

// Normal filing fee for a document filed by a company HAVING share capital,
// by NOMINAL (authorised) share capital. Annexure, Table of Fees, item I.
// `belowCapital` is exclusive, matching the rule's own wording ("less than
// 1,00,000" / "1,00,000 to 4,99,999" / … / "1,00,00,000 or more").
const COMPANY_NORMAL_FEE_SLABS = [
  { belowCapital: 100000, fee: 200 },
  { belowCapital: 500000, fee: 300 },
  { belowCapital: 2500000, fee: 400 },
  { belowCapital: 10000000, fee: 500 },
  { belowCapital: Infinity, fee: 600 },
];

// Assumed normal fee when authorised capital is unknown. CONSERVATIVE — the TOP
// slab, not a middle guess — by the same convention as `smallLlp = false` above:
// an estimate that reads LOW is the one that gets a client into trouble, so an
// unknown resolves to the highest fee the rule can charge.
//
// A stored 0 counts as unknown, not as a genuine ₹0 company: MCA master data
// falls back to '0' when the amount is not published, which is why
// MCA_APPLY_FIELDS (companyController.js) also treats 0 as absent.
const COMPANY_DEFAULT_NORMAL_FEE = 600;

// Additional fee = multiplier × normal filing fee, by delay band.
const COMPANY_ADDITIONAL_FEE_BANDS = [
  { maxDays: 15, multiplier: 1 },
  { maxDays: 30, multiplier: 2 },
  { maxDays: 60, multiplier: 4 },
  { maxDays: 90, multiplier: 6 },
  { maxDays: 180, multiplier: 10 },
  { maxDays: Infinity, multiplier: 12 },
];

// The 1× first band is a CARVE-OUT, not a general grace period. The Annexure
// writes that row as "upto 15 days (sections 139 and 157)" — two sections only.
// ADT-1 is filed under section 139, so it does get the 1× band; every other form
// routed through `companies2018` starts at 2× from day one.
const COMPANY_FIRST_BAND_SECTIONS = ['Sec 139', 'Sec 157'];

function companyNormalFee(authorisedCapital) {
  const c = Number(authorisedCapital);
  if (!(c > 0)) return COMPANY_DEFAULT_NORMAL_FEE;
  const slab = COMPANY_NORMAL_FEE_SLABS.find((s) => c < s.belowCapital);
  return slab ? slab.fee : COMPANY_DEFAULT_NORMAL_FEE;
}

/**
 * Additional fee for a late Companies Act filing.
 *
 * @param {number} daysOverdue
 * @param {object} [opts]
 * @param {number|null} [opts.authorisedCapital] Nominal share capital in ₹.
 *   Unknown / 0 ⇒ the top fee slab (see COMPANY_DEFAULT_NORMAL_FEE).
 * @param {string} [opts.section] The form's statutory section, which decides
 *   whether the 1× ≤15-day carve-out applies.
 * @returns {number} Additional fee in ₹ (a total, not a rate).
 */
function estimateCompanySlabPenalty(daysOverdue, { authorisedCapital = null, section = '' } = {}) {
  if (daysOverdue <= 0) return 0;
  const fee = companyNormalFee(authorisedCapital);
  const band = COMPANY_ADDITIONAL_FEE_BANDS.find((b) => daysOverdue <= b.maxDays);
  if (band.maxDays === 15 && !COMPANY_FIRST_BAND_SECTIONS.includes(String(section).trim())) {
    return COMPANY_ADDITIONAL_FEE_BANDS[1].multiplier * fee;
  }
  return band.multiplier * fee;
}

// ─── Form Registry ───────────────────────────────────────────

/**
 * Each form entry defines:
 *   code         — official MCA form code
 *   name         — short description
 *   section      — Companies Act section / rule
 *   requiresAgm  — true if deadline is computed relative to AGM date
 *   computeDue   — (agmDate: Date, fyEndDate: Date) => Date
 *   penalty      — { perDay, max, flat, note, slabs? }
 *   colorClass   — Tailwind accent colour class for UI
 */

// Date a human last verified every figure below against the MCA portal /
// India Code / the LLP Rules. Bump it whenever the registry is re-checked;
// the chat layer surfaces it as `dataAsOf` and uses it to decide when a
// table answer needs a web cross-check (stale-while-revalidate).
// 2026-07-30: ADT-1 re-verified and its penalty rewritten from a ₹300/day proxy
// to the actual slab regime (COMPANY_ADDITIONAL_FEE_BANDS above). Bands and the
// "upto 15 days (sections 139 and 157)" carve-out cross-checked against four
// independent renderings of the Annexure to the Companies (Registration Offices
// and Fees) Rules 2014 — Indian Kanoon, CAIRR (ca2013.com) rule 12, Taxmann, and
// IndiaFilings' write-up of G.S.R. 435(E) — which agree on 1×/2×/4×/6×/10×/12×
// and on the nominal-capital fee slabs ₹200/300/400/500/600.
//
// 2026-07-31: DPT-3 (Rule 16, Companies (Acceptance of Deposits) Rules 2014),
// MSME-1 (Sec 405 order dt 22 Jan 2019 as amended) and BEN-2 (Sec 90 read with
// Rule 4, Companies (Significant Beneficial Owners) Rules 2018) added. Only
// DPT-3 becomes a dated checklist row: MSME-1 turns on a fact we do not hold
// (>45-day dues to a micro/small enterprise) and BEN-2 turns on an event
// (receipt of a BEN-1 declaration), so both are surfaced as scope notes rather
// than as deadlines that could report a company overdue for an obligation it
// may not even have. No late-fee FIGURES are asserted for any of the three —
// the fee is the Registration Offices and Fees Rules table, which is already
// modelled for the forms where we hold the capital it depends on.
const REGISTRY_AS_OF = '2026-07-31';

// ─── MSME-1 Half-Year Cadence ────────────────────────────────

/**
 * The NEXT MSME-1 due date on or after `today`.
 *
 * Two returns a year, each covering the half-year that has just closed:
 *   October–March half-year  → due 30 April (of the following calendar year)
 *   April–September half-year → due 31 October (of the same calendar year)
 *
 * Both candidates in the current calendar year are computed and the first one
 * not yet past is taken; if both are past, the cycle rolls into next April. That
 * roll is what makes the December→April boundary work — on 31 Dec 2026 the next
 * due date is 30 Apr 2027, not a date already gone by.
 *
 * "On or after" is deliberate: a form due TODAY is not yet late, and returning
 * the following period on the due date itself would silently skip a filing.
 */
function computeMsme1NextDue(today) {
  const now = startOfDay(today) || startOfDay(new Date());
  const y = now.getFullYear();
  const candidates = [
    new Date(y, 3, 30),      // 30 Apr — Oct–Mar half-year
    new Date(y, 9, 31),      // 31 Oct — Apr–Sep half-year
    new Date(y + 1, 3, 30),  // roll into next year's April
  ];
  return candidates.find((d) => d >= now) || candidates[candidates.length - 1];
}

const FORM_REGISTRY = {
  aoc4: {
    code: 'AOC-4',
    name: 'Financial Statements',
    section: 'Sec 137',
    requiresAgm: true,
    computeDue: (agm) => addDays(agm, 30),
    penalty: { perDay: 100, max: null, flat: 0, note: '\u20B9100/day (no max cap)' },
    colorClass: 'text-[#145f5f]',
  },
  // XBRL is NOT a blanket public-company obligation — it applies to listed
  // companies and their Indian subsidiaries, paid-up capital ≥ ₹5 cr, turnover
  // ≥ ₹100 cr, or Ind-AS filers (Companies (Filing of Documents and Forms in
  // XBRL) Rules, 2015). No entity type maps to this form statically: it is
  // selected per company by determineXbrl(), which substitutes it for `aoc4` in
  // computeFilingDeadlines. Undecidable cases keep plain AOC-4 + XBRL_HINT.
  aoc4xbrl: {
    code: 'AOC-4 XBRL',
    name: 'Financial Statements (XBRL)',
    section: 'Sec 137',
    requiresAgm: true,
    computeDue: (agm) => addDays(agm, 30),
    penalty: { perDay: 100, max: null, flat: 0, note: '\u20B9100/day (no max cap)' },
    colorClass: 'text-[#145f5f]',
  },
  mgt7: {
    code: 'MGT-7',
    name: 'Annual Return',
    section: 'Sec 92',
    requiresAgm: true,
    computeDue: (agm) => addDays(agm, 60),
    penalty: { perDay: 100, max: null, flat: 0, note: '\u20B9100/day (no max cap)' },
    colorClass: 'text-[#3a4a66]',
  },
  mgt7a: {
    code: 'MGT-7A',
    name: 'Annual Return (OPC / Small Co.)',
    section: 'Sec 92',
    requiresAgm: true,
    computeDue: (agm) => addDays(agm, 60),
    penalty: { perDay: 100, max: null, flat: 0, note: '\u20B9100/day (no max cap)' },
    colorClass: 'text-[#3a4a66]',
  },
  adt1: {
    code: 'ADT-1',
    name: 'Auditor Appointment',
    section: 'Sec 139',
    requiresAgm: true,
    computeDue: (agm) => addDays(agm, 15),
    // No per-day component at all: the additional fee is a MULTIPLE of the
    // capital-based normal filing fee, stepped by delay band \u2014 1\u00D7 within 15 days
    // (ADT-1 is a section 139 filing, so it gets the carve-out first band),
    // rising to 12\u00D7 beyond 180 days and stopping there.
    penalty: { perDay: 0, max: null, flat: 0, slabs: 'companies2018', note: '1\u00D7\u201312\u00D7 normal fee by delay band (capital-based)' },
    colorClass: 'text-[#836016]',
  },
  // DIR-12 is EVENT-based (Sec 170: 30 days from a director/KMP appointment,
  // cessation or change), never an annual AGM-relative filing. It is kept in the
  // registry for lookups but is deliberately absent from every ENTITY_FORMS list
  // \u2014 see EVENT_BASED_FORMS, which the checklist surfaces as a note instead.
  dir12: {
    code: 'DIR-12',
    name: 'Director / KMP Change',
    section: 'Sec 170',
    requiresAgm: false,
    computeDue: () => null,
    // Actual MCA late fee is 2\u00D7\u201312\u00D7 the normal fee (delay bands) plus a Sec 172
    // adjudication penalty (\u20B9500/day continuing, company AND officer, capped).
    penalty: { perDay: 0, max: null, flat: 0, note: '2\u00D7\u201312\u00D7 normal fee + Sec 172 penalty' },
    colorClass: 'text-[#6f6049]',
  },
  // DPT-3 — Rule 16 of the Companies (Acceptance of Deposits) Rules, 2014, made
  // under Sec 73. An ANNUAL return of deposits AND of money received that is not
  // considered a deposit, due 30 June for the FY that closed on the preceding
  // 31 March. It is not AGM-relative and it does not depend on whether the
  // company actually took deposits — a company with only exempted receipts still
  // files the same return — so it is a genuine calendar row for every company,
  // and only for companies (an LLP has no Sec 73 obligation).
  //
  // Fixed-date pattern is ITR-6's: derive the filing year from the FY close,
  // then pin the day. No penalty FIGURE is stated: the additional fee is the
  // Registration Offices and Fees Rules table, which is capital-dependent, and
  // inventing a per-day number here is exactly the error the ADT-1 rewrite (see
  // REGISTRY_AS_OF) was correcting.
  dpt3: {
    code: 'DPT-3',
    name: 'Return of Deposits',
    section: 'Sec 73',
    requiresAgm: false,
    computeDue: (_agm, fyEnd) => {
      const yr = fyEnd ? (fyEnd.getMonth() <= 2 ? fyEnd.getFullYear() : fyEnd.getFullYear() + 1) : new Date().getFullYear();
      return new Date(yr, 5, 30); // 30 June
    },
    penalty: { perDay: 0, max: null, flat: 0, note: 'Late filing fees under the Companies (Registration Offices and Fees) Rules' },
    colorClass: 'text-[#836016]',
  },
  // MSME-1 — Sec 405 order (22 Jan 2019, as amended). HALF-YEARLY, and
  // deliberately NOT in any ENTITY_FORMS list: the return is required only from
  // a company that has amounts outstanding to a micro or small enterprise
  // supplier for MORE THAN 45 DAYS, and nothing in company master data or in the
  // record we hold reveals that. Listing it as a dated row would report every
  // company overdue twice a year for an obligation most of them do not have, so
  // it is surfaced through EVENT_BASED_FORMS as a scope note instead.
  //
  // `computeDue` still exists and still returns the NEXT upcoming due date, so a
  // consumer that has established applicability (a user, or a future
  // "we owe an MSME" flag) gets the right date without re-deriving the cadence.
  msme1: {
    code: 'MSME-1',
    name: 'Half-yearly Return of MSME Dues',
    section: 'Sec 405',
    requiresAgm: false,
    computeDue: (_agm, _fyEnd, today) => computeMsme1NextDue(today),
    penalty: { perDay: 0, max: null, flat: 0, note: 'Penalty under Sec 405(4) for failure to furnish the return' },
    colorClass: 'text-[#6f6049]',
  },
  // BEN-2 — Sec 90 read with Rule 4 of the Companies (Significant Beneficial
  // Owners) Rules, 2018: due within 30 days of the company RECEIVING a
  // declaration in Form BEN-1. Event-based exactly like DIR-12, so it takes the
  // same shape — a registry entry for lookups, no ENTITY_FORMS membership, and a
  // scope note in EVENT_BASED_FORMS.
  ben2: {
    code: 'BEN-2',
    name: 'Return of Significant Beneficial Owners',
    section: 'Sec 90',
    requiresAgm: false,
    computeDue: () => null,
    penalty: { perDay: 0, max: null, flat: 0, note: 'Late filing fees under the Companies (Registration Offices and Fees) Rules + Sec 90(11) penalty' },
    colorClass: 'text-[#6f6049]',
  },
  dir3kyc: {
    code: 'DIR-3 KYC',
    name: 'Director KYC',
    section: 'Rule 12A',
    requiresAgm: false,
    // Triennial cycle w.e.f. 31 Mar 2026 (Rule 12A substituted by the Companies
    // (Appointment and Qualification of Directors) Amendment Rules 2025, G.S.R.
    // 943(E) dt 31 Dec 2025; fees via G.S.R. 300(E) dt 21 Apr 2026): DIR-3 KYC Web
    // is due once every third consecutive FY, on or before 30 JUNE (not 30 Sep).
    // Directors compliant under the old annual regime (last filed by 30 Sep 2025)
    // next file by 30 Jun 2028, then 2031, 2034… — so no portfolio-wide filing
    // falls due in 2026/2027. Per-director offsets (e.g. DIN allotted FY 2025-26
    // → first due 30 Jun 2029) and interim mobile/email/address changes (30-day
    // web filing, ₹500) can't be modelled without per-DIN data; we show the
    // 2028+3k grid as the portfolio default. Seasons ≤ 2025 keep the old 30 Sep
    // annual due date so historical dashboards stay correct.
    computeDue: (_agm, fyEnd) => {
      const yr = fyEnd ? (fyEnd.getMonth() <= 2 ? fyEnd.getFullYear() : fyEnd.getFullYear() + 1) : new Date().getFullYear();
      if (yr <= 2025) return new Date(yr, 8, 30);
      const cycleYr = yr <= 2028 ? 2028 : 2028 + Math.ceil((yr - 2028) / 3) * 3;
      return new Date(cycleYr, 5, 30);
    },
    penalty: { perDay: 0, max: null, flat: 5000, note: '\u20B95,000 flat + DIN deactivated' },
    colorClass: 'text-[#3a4a66]',
  },
  itr6: {
    code: 'ITR-6',
    name: 'Income Tax Return (Companies)',
    section: 'IT Act',
    requiresAgm: false,
    computeDue: (_agm, fyEnd) => {
      const yr = fyEnd ? (fyEnd.getMonth() <= 2 ? fyEnd.getFullYear() : fyEnd.getFullYear() + 1) : new Date().getFullYear();
      return new Date(yr, 9, 31); // 31 October
    },
    penalty: { perDay: 0, max: null, flat: 5000, note: 'Interest u/s 234A + late fee u/s 234F' },
    colorClass: 'text-[#4a6b3f]',
  },
  itr7: {
    code: 'ITR-7',
    name: 'Income Tax Return (Sec 8 / Trust)',
    section: 'IT Act',
    requiresAgm: false,
    computeDue: (_agm, fyEnd) => {
      const yr = fyEnd ? (fyEnd.getMonth() <= 2 ? fyEnd.getFullYear() : fyEnd.getFullYear() + 1) : new Date().getFullYear();
      return new Date(yr, 9, 31);
    },
    penalty: { perDay: 0, max: null, flat: 5000, note: 'Interest u/s 234A + late fee u/s 234F' },
    colorClass: 'text-[#4a6b3f]',
  },
  itr5: {
    code: 'ITR-5',
    name: 'Income Tax Return (LLP)',
    section: 'IT Act',
    requiresAgm: false,
    computeDue: (_agm, fyEnd) => {
      // 31 October for audit cases (safer default)
      const yr = fyEnd ? (fyEnd.getMonth() <= 2 ? fyEnd.getFullYear() : fyEnd.getFullYear() + 1) : new Date().getFullYear();
      return new Date(yr, 9, 31);
    },
    penalty: { perDay: 0, max: null, flat: 5000, note: 'Interest u/s 234A + late fee u/s 234F' },
    colorClass: 'text-[#4a6b3f]',
  },
  form8: {
    code: 'LLP Form 8',
    name: 'Statement of Account & Solvency',
    // Sec 34(3), not Sec 35 — s.34(2) requires the Statement of Account and
    // Solvency to be prepared within six months of the FY end and s.34(3)
    // requires it to be filed. Sec 35 is the ANNUAL RETURN, which is form11.
    section: 'LLP Act Sec 34(3)',
    requiresAgm: false,
    computeDue: (_agm, fyEnd) => {
      // Within 30 days from end of 6 months from FY close => 30 October for March FY
      const sixMonths = addMonths(fyEnd, 6);
      return addDays(sixMonths, 30);
    },
    // LLP additional fees moved to delay-band slabs (\u00D7 normal fee, small LLPs half) with
    // the LLP (Amendment) Rules 2022 \u2014 flat \u20B9100/day only applied up to FY 2020-21.
    // slabs: 'llp2022' routes estimatePenalty through the band table above.
    penalty: { perDay: 0, max: null, flat: 0, slabs: 'llp2022', note: 'Slab-based \u00D7 normal fee (small LLPs half)' },
    colorClass: 'text-[#8a6a1f]',
  },
  form11: {
    code: 'LLP Form 11',
    name: 'Annual Return (LLP)',
    // Sec 35(1) — due within sixty days of the close of the financial year.
    // "LLP Rules" was not a citation anyone could follow.
    section: 'LLP Act Sec 35(1)',
    requiresAgm: false,
    computeDue: (_agm, fyEnd) => {
      // Within 60 days from close of FY => 30 May for March FY
      return addDays(fyEnd, 60);
    },
    penalty: { perDay: 0, max: null, flat: 0, slabs: 'llp2022', note: 'Slab-based \u00D7 normal fee (small LLPs half)' },
    colorClass: 'text-[#145f5f]',
  },
};

// ─── Entity -> Applicable Forms ──────────────────────────────

// DPT-3 is on every COMPANY list and on no LLP list: Sec 73 and the Deposit
// Rules are Companies Act instruments, and an LLP has no return of deposits.
const ENTITY_FORMS = {
  pvtlimited: ['aoc4', 'mgt7', 'adt1', 'dpt3', 'dir3kyc', 'itr6'],
  public:     ['aoc4', 'mgt7', 'adt1', 'dpt3', 'dir3kyc', 'itr6'],
  opc:        ['aoc4', 'mgt7a', 'adt1', 'dpt3', 'dir3kyc', 'itr6'],
  sec8:       ['aoc4', 'mgt7', 'adt1', 'dpt3', 'dir3kyc', 'itr7'],
  llp:        ['form8', 'form11', 'dir3kyc', 'itr5'],
};

const EVENT_BASED_FORMS = [
  {
    code: 'DIR-12',
    section: 'Sec 170',
    note: 'Due within 30 days of any director or KMP appointment, cessation or change in designation — it is not an annual filing.',
  },
  {
    code: 'BEN-2',
    section: 'Sec 90',
    note: 'Due within 30 days of the company receiving a declaration in Form BEN-1 from a significant beneficial owner — it is an event filing, not an annual one.',
  },
  {
    code: 'MSME-1',
    section: 'Sec 405',
    note: 'Half-yearly return of outstanding dues to micro and small enterprises — due 30 April for the October–March half-year and 31 October for the April–September half-year. It is required only where amounts payable to an MSME supplier have been outstanding for more than 45 days, which cannot be determined from company master data, so it is not tracked here as a dated deadline.',
  },
];

// Shown only when the XBRL test genuinely cannot be settled from what we hold
// (see determineXbrl → basis 'unknown'). Not a blanket public-company caption.
const XBRL_HINT = 'AOC-4 XBRL may apply instead of AOC-4 if the company is listed (or is an Indian subsidiary of a listed company), has paid-up capital of ₹5 crore or more, turnover of ₹100 crore or more, or prepares its accounts under Ind-AS.';

/**
 * Whether a company must file its financial statements in XBRL, per Rule 3 of
 * the Companies (Filing of Documents and Forms in XBRL) Rules, 2015: listed
 * companies and their Indian subsidiaries, paid-up capital ≥ ₹5 crore,
 * turnover ≥ ₹100 crore, or companies reporting under Ind AS.
 *
 * DELIBERATELY OPT-IN. XBRL was previously assumed for every public company,
 * which put an inapplicable form — and its penalty — on the checklist of any
 * public company below the thresholds. A company we cannot positively place
 * above a threshold files ordinary AOC-4, which is the safe default: showing a
 * form that is not owed is worse than omitting one the user can add.
 */
function requiresXbrl(company = {}) {
  if (!company || typeof company !== 'object') return false;
  // Explicit override always wins — the CS team knows better than a threshold.
  if (typeof company.xbrlApplicable === 'boolean') return company.xbrlApplicable;
  if (/^yes/i.test(String(company.listed || ''))) return true;
  if (company.indAsApplicable === true) return true;
  if (company.subsidiaryOfListed === true) return true;
  const paidUp = Number(company.paidUpCapital) || 0;
  if (paidUp >= XBRL_PAID_UP_THRESHOLD) return true;
  const turnover = Number(company.turnoverInr ?? company.turnover) || 0;
  if (turnover >= XBRL_TURNOVER_THRESHOLD) return true;
  return false;
}

/** 'aoc4' → 'aoc4xbrl' only when the XBRL rules actually bite. */
const resolveAoc4Key = (key, company) =>
  (key === 'aoc4' && requiresXbrl(company) ? 'aoc4xbrl' : key);

// Entities that are required to hold an AGM
const AGM_REQUIRED = { pvtlimited: true, public: true, sec8: true, opc: false, llp: false };

// Entity display labels
const ENTITY_LABELS = {
  pvtlimited: 'Private Limited',
  public: 'Public Limited',
  opc: 'One Person Company',
  sec8: 'Section 8 / NGO',
  llp: 'LLP',
};

// ─── AOC-4 XBRL Applicability ────────────────────────────────

// Companies (Filing of Documents and Forms in XBRL) Rules, 2015, Rule 3.
const XBRL_PAID_UP_THRESHOLD = 50000000; // ₹5 crore
const XBRL_TURNOVER_THRESHOLD = 1000000000; // ₹100 crore

/** "5,00,00,000" / "50000000.00" / 5e7 → 50000000. Unparseable/absent → null. */
function parseInrAmount(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/[,\s₹]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/** 50000000 → "5", 62500000 → "6.25" — crore, trailing zeros trimmed. */
function formatCrore(amountInr) {
  const cr = amountInr / 10000000;
  return String(Math.round(cr * 100) / 100);
}

/**
 * Listing status encoded in the CIN itself: first character is L for a listed
 * company, U for an unlisted one. MCA reissues the CIN when a company lists or
 * delists, so this tracks the registry position and is the signal practitioners
 * read first. Non-CIN identifiers (LLPIN / FCRN) carry no such class → null.
 */
function cinListingClass(cin) {
  const c = String(cin || '').replace(/\s+/g, '').toUpperCase();
  if (!/^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(c)) return null;
  return c[0] === 'L' ? 'listed' : 'unlisted';
}

/**
 * Decide whether financial statements go on AOC-4 XBRL instead of plain AOC-4.
 *
 * Rule 3 is an OR of four limbs — listed (or Indian subsidiary of a listed
 * company), paid-up capital ≥ ₹5 cr, turnover ≥ ₹100 cr, or Ind-AS filer — so
 * ANY single true limb settles it and the rest need not be asked. Two of the
 * four (listing status, paid-up capital) come straight from MCA master data,
 * which is why most companies resolve with no questions at all.
 *
 * The remaining two are NOT registry fields: turnover lives in the financial
 * statements and Ind-AS adoption is nowhere in master data. So the only thing
 * ever asked is one optional yes/no — turnover ≥ ₹100 cr — and only when the
 * registry limbs are both known-false. Ind-AS stays in XBRL_HINT prose; a
 * company that files under Ind-AS is captured by the manual override.
 *
 * Provenance over more data entry: master data reflects the LAST FILED
 * position, so a recent allotment can leave paid-up capital stale. Every
 * determination therefore carries its basis, its source and — for registry
 * facts — the as-of date of the fetch it came from, and can be overridden.
 *
 * Sticky rule: a company that has once filed in XBRL must keep filing in XBRL
 * even after dropping below every threshold (`xbrlFiledPreviously`).
 *
 * Returns `required: null` when genuinely undecidable — the caller keeps the
 * honest "may apply" hint rather than guessing either way.
 */
function determineXbrl(company) {
  const src = company || {};
  const asOf = toDate(src.mcaDataAsOf);
  const asOfFormatted = asOf ? formatDateEN(asOf) : '';
  const registryStamp = asOf ? ` per MCA master data, fetched ${asOfFormatted}` : ' per MCA master data (fetch date unknown)';

  const paidUp = parseInrAmount(src.paidUpCapital);
  const listedField = String(src.listed || '').trim();
  const cinClass = cinListingClass(src.cin);
  // A positive assertion from either side counts. `listed` defaults to 'No' on
  // the model, so a bare 'No' is weak evidence — but 'No' AND a U-class CIN
  // together are a genuine known-false.
  const listedYes = /^yes/i.test(listedField) || cinClass === 'listed';
  const listedKnownNo = !listedYes && (/^no$/i.test(listedField) && cinClass !== null);

  // Turnover: the explicit yes/no answer, or a stored figure if one exists.
  const turnoverInr = parseInrAmount(src.turnoverInr ?? src.turnover);
  const turnoverAnswered = /^(yes|no)$/i.test(String(src.turnoverAbove100Cr || '').trim());
  let turnoverAnswer = null;
  if (/^yes$/i.test(String(src.turnoverAbove100Cr || ''))) turnoverAnswer = true;
  else if (/^no$/i.test(String(src.turnoverAbove100Cr || ''))) turnoverAnswer = false;
  else if (turnoverInr !== null && turnoverInr > 0) turnoverAnswer = turnoverInr >= XBRL_TURNOVER_THRESHOLD;

  const build = (required, basis, reason, source) => ({
    required,
    formKey: required === true ? 'aoc4xbrl' : 'aoc4',
    basis,
    reason,
    source,
    asOf: source === 'mca' ? asOf : null,
    asOfFormatted: source === 'mca' ? asOfFormatted : '',
    hint: required === null ? XBRL_HINT : '',
    // The one question that could still settle an undecided case.
    askTurnover: required === null && turnoverAnswer === null,
    // The turnover limb, exposed so the UI can keep the question visible AFTER
    // it is answered and offer a way back to "not sure". Without this the
    // control disappeared the moment it was used and a mis-tapped yes/no was
    // unclearable — an un-answer trap. `turnoverAnswered` is true only when the
    // value came from the stored yes/no (which the UI can clear), NOT when it
    // was inferred from a turnover FIGURE, which no clear button could undo.
    turnoverAnswer,
    turnoverAnswered,
  });

  // 1. Manual override outranks everything, in both directions.
  const override = String(src.xbrlOverride || '').trim();
  if (override === 'required') {
    return build(true, 'override', 'AOC-4 XBRL required — set manually for this company.', 'manual');
  }
  if (override === 'not_required') {
    return build(false, 'override', 'Plain AOC-4 — XBRL marked not applicable manually for this company.', 'manual');
  }

  // 2. Sticky: once in XBRL, always in XBRL. Cannot flip a company OUT.
  if (src.xbrlFiledPreviously === true) {
    return build(true, 'once-filed', 'AOC-4 XBRL required — this company has filed in XBRL before, so it must continue in XBRL even if it now falls below every threshold.', 'manual');
  }

  // 3–4. Registry limbs — these short-circuit most companies with no questions.
  if (listedYes) {
    const how = /^yes/i.test(listedField) ? '' : ' (CIN class L)';
    return build(true, 'listed', `AOC-4 XBRL required — listed company${how},${registryStamp}.`, 'mca');
  }
  if (paidUp !== null && paidUp >= XBRL_PAID_UP_THRESHOLD) {
    return build(true, 'paid-up', `AOC-4 XBRL required — paid-up capital ₹${formatCrore(paidUp)} cr (threshold ₹5 cr),${registryStamp}.`, 'mca');
  }

  // 5. The one asked question.
  if (turnoverAnswer === true) {
    return build(true, 'turnover', 'AOC-4 XBRL required — turnover is ₹100 crore or more.', 'manual');
  }

  // 6. Every limb we can see is false, and the one we asked came back no.
  const paidUpKnown = paidUp !== null && paidUp > 0;
  if (listedKnownNo && paidUpKnown && paidUp < XBRL_PAID_UP_THRESHOLD && turnoverAnswer === false) {
    return build(false, 'below-thresholds', `Plain AOC-4 — unlisted, paid-up capital ₹${formatCrore(paidUp)} cr and turnover under ₹100 crore. Confirm Ind-AS does not apply.`, 'mca');
  }

  // 7. Undecidable — say so rather than guess.
  return build(null, 'unknown', XBRL_HINT, '');
}

// ─── Threshold-Driven Obligation Hints ───────────────────────
//
// Everything above this line fires off DATES. This block fires off FIGURES, and
// figures are the one thing this platform mostly does not hold: MCA master data
// publishes authorised and paid-up capital and nothing else. Net worth, net
// profit, borrowings, deposits, reserves and revenue are simply absent, and
// turnover is present only when a user typed it.
//
// So the output here is deliberately NOT a checklist. It is a set of HINTS, each
// carrying (a) the statutory test in full, (b) which limbs of that test we could
// actually evaluate, and (c) a three-valued status — never a bare yes/no:
//
//   'applies'       every limb needed to say YES was present and met.
//   'may_apply'     a limb that could flip the answer is not on record.
//   'exempt_likely' a limb we CAN see rules the obligation out on the evidence
//                   held, while unseen limbs remain unseen — "likely", not "no".
//
// The asymmetry is intentional: 'applies' is a claim, so it needs positive data;
// 'may_apply' is an admission, so it is the safe default. A hint never says
// "you are exempt" outright, because none of these exemptions can be established
// from what we hold.

/**
 * A POSITIVE rupee figure, or null for "unknown".
 *
 * The zero rule is the whole point. MCA master data writes '0' when an amount is
 * not published, and the model defaults capital fields to 0 — so a stored zero
 * is evidence of NOTHING, not evidence of a zero-rupee company. Treating it as a
 * real figure would let "turnover 0" satisfy "turnover ≤ ₹100 crore" and quietly
 * certify an unknown company as small. The same convention already governs
 * COMPANY_DEFAULT_NORMAL_FEE and MCA_APPLY_FIELDS.
 */
function parsePositiveInr(value) {
  const n = parseInrAmount(value);
  return n !== null && n > 0 ? n : null;
}

// Companies (Specification of Definitions Details) Amendment Rules, 2025,
// effective 1 December 2025 — Sec 2(85) small-company limits raised to ₹10 cr
// paid-up / ₹100 cr turnover (from ₹4 cr / ₹40 cr).
const SMALL_CO_PAID_UP_LIMIT = 100000000;    // ₹10 crore
const SMALL_CO_TURNOVER_LIMIT = 1000000000;  // ₹100 crore
// ─── Small company — Sec 2(85) → MGT-7A instead of MGT-7 ───────────────────
//
// Sec 2(85) (thresholds per the Companies (Specification of Definitions
// Details) Amendment Rules, 2025, effective 1 December 2025): a company other
// than a public company with paid-up capital not exceeding ₹10 crore AND
// turnover not exceeding ₹100 crore, unless it is a holding or subsidiary
// company, a Sec 8 company, or governed by a special Act.
//
// Rule 11(1) of the Companies (Management and Administration) Rules, 2014 lets
// a small company file its annual return in MGT-7A. The checklist used to show
// MGT-7 with a note saying MGT-7A was available; the client asked (21 Sep 2026)
// for the applicable form to be shown outright. So: when the figures settle
// it, the MGT-7 row becomes MGT-7A. When either figure is missing it stays
// MGT-7 — the fuller obligation — with the "may apply" hint, never a guess.
//
// The proviso exclusions are tri-state on the company record (null = not
// answered). A known-true exclusion makes the company not small. An unanswered
// one does not block the swap — nearly every private company is neither a
// holding nor a subsidiary — but the row says the swap assumes it, so the
// officer knows what to correct if it is wrong.
function determineSmallCompany(company, companyMeta) {
  const src = company || {};
  const meta = companyMeta || {};
  const entity = normalizeEntityType(src.type);
  const paidUp = parsePositiveInr(src.paidUpCapital);
  const turnover = parsePositiveInr(meta.turnoverInr ?? meta.turnover ?? src.turnoverInr ?? src.turnover);
  const yes = (v) => v === true || /^(yes|true)$/i.test(String(v ?? ''));
  const unanswered = (v) => v === null || v === undefined || v === '';
  const excluded = yes(src.isHoldingCompany) || yes(src.isSubsidiaryCompany) || yes(src.governedBySpecialAct);
  const exclusionsUnknown = unanswered(src.isHoldingCompany) || unanswered(src.isSubsidiaryCompany) || unanswered(src.governedBySpecialAct);

  const build = (small, reason) => ({
    small,
    formKey: small === true ? 'mgt7a' : 'mgt7',
    paidUp,
    turnover,
    exclusionsUnknown: small === true && exclusionsUnknown,
    reason,
  });

  if (entity !== 'pvtlimited') return build(false, entity === 'opc' ? 'OPC files MGT-7A under Rule 11(1) in its own right.' : 'Sec 2(85) applies to companies other than public companies; Sec 8 companies are excluded by the proviso.');
  if (excluded) return build(false, 'Excluded by the Sec 2(85) proviso: a holding or subsidiary company, or one governed by a special Act, is not a small company.');
  const overPaidUp = paidUp !== null && paidUp > SMALL_CO_PAID_UP_LIMIT;
  const overTurnover = turnover !== null && turnover > SMALL_CO_TURNOVER_LIMIT;
  if (overPaidUp || overTurnover) return build(false, `${overPaidUp && overTurnover ? 'Paid-up capital and turnover both exceed' : overPaidUp ? 'Paid-up capital exceeds' : 'Turnover exceeds'} the Sec 2(85) limit.`);
  if (paidUp === null || turnover === null) return build(null, `${paidUp === null && turnover === null ? 'Paid-up capital and turnover are' : paidUp === null ? 'Paid-up capital is' : 'Turnover is'} not on record, so small-company status is unsettled; MGT-7 is shown until it is.`);
  return build(true, 'Paid-up capital ≤ ₹10 crore and turnover ≤ ₹100 crore: a small company under Sec 2(85); annual return in MGT-7A (Rule 11(1)).');
}

// Sec 135(1) — any one limb triggers CSR.
const CSR_NET_WORTH_THRESHOLD = 5000000000;  // ₹500 crore
const CSR_TURNOVER_THRESHOLD = 10000000000;  // ₹1,000 crore
const CSR_NET_PROFIT_THRESHOLD = 50000000;   // ₹5 crore

// Sec 204 read with Rule 9 — secretarial audit (Form MR-3).
const MR3_PAID_UP_THRESHOLD = 500000000;     // ₹50 crore
const MR3_TURNOVER_THRESHOLD = 2500000000;   // ₹250 crore
const MR3_BORROWINGS_THRESHOLD = 1000000000; // ₹100 crore

// Sec 138 read with Rule 13 — internal audit.
const IA_PAID_UP_THRESHOLD = 500000000;      // ₹50 crore
const IA_TURNOVER_THRESHOLD = 2000000000;    // ₹200 crore
const IA_BORROWINGS_THRESHOLD = 1000000000;  // ₹100 crore
const IA_DEPOSITS_THRESHOLD = 250000000;     // ₹25 crore

// Sec 148 read with the Companies (Cost Records and Audit) Rules, 2014.
const COST_RECORDS_TURNOVER_THRESHOLD = 350000000; // ₹35 crore

// CARO 2020, paragraph 1(2) — the private-company carve-out.
const CARO_PAID_UP_RESERVES_LIMIT = 10000000; // ₹1 crore
const CARO_BORROWINGS_LIMIT = 10000000;       // ₹1 crore
const CARO_REVENUE_LIMIT = 100000000;         // ₹10 crore

// Notification G.S.R. 583(E) dt 13 Jun 2017 — private-company IFC reporting.
const IFC_TURNOVER_LIMIT = 500000000;   // ₹50 crore
const IFC_BORROWINGS_LIMIT = 250000000; // ₹25 crore

const UNKNOWN_FIGURE_CAVEAT = 'A stored 0 is treated as unknown, not as a genuine zero — MCA master data publishes 0 when an amount is withheld.';

/** ₹ figure → "₹10 cr" / "₹6.25 cr", or the literal 'not on record'. */
function croreOrUnknown(amountInr) {
  return amountInr === null ? 'not on record' : `₹${formatCrore(amountInr)} cr`;
}

/**
 * Threshold-driven obligations that this platform can HINT at but cannot decide.
 *
 * @param {object} company      the company record (type, cin, listed, capital…)
 * @param {object} [companyMeta] optional overrides — `{ turnoverInr }` in the
 *   same shape computeFilingDeadlines already accepts, so a caller that has a
 *   fresher turnover than the stored one can pass it without mutating the record.
 * @returns {Array<{key: string, label: string, basis: string, status: string}>}
 *   `status` ∈ 'applies' | 'may_apply' | 'exempt_likely'. The small-company hint
 *   additionally carries `formKey` + `note` when it applies, so a consumer can
 *   attach the MGT-7A note to the right checklist row (see attachObligationNotes).
 */
function computeObligationHints(company, companyMeta) {
  const src = company || {};
  const meta = companyMeta || {};
  const entity = normalizeEntityType(src.type);

  // Every test below is a Companies Act 2013 test. An LLP is governed by the LLP
  // Act and none of them reach it — an empty list is the honest answer, not a
  // list of "may apply" hints about statutes that do not apply.
  if (entity === 'llp') return [];

  const paidUp = parsePositiveInr(src.paidUpCapital);
  const turnover = parsePositiveInr(
    meta.turnoverInr ?? meta.turnover ?? src.turnoverInr ?? src.turnover
  );
  const listed = cinListingClass(src.cin) === 'listed' || /^yes/i.test(String(src.listed || '').trim());
  const isPublic = entity === 'public';
  const isPrivate = entity === 'pvtlimited';

  const hints = [];

  // ── Small company — Sec 2(85) ──────────────────────────────
  //
  // Only for private companies, and that is the statute's own scope, not a
  // simplification: Sec 2(85) opens "company, other than a public company", and
  // the proviso further excludes holding/subsidiary companies, Sec 8 companies
  // and companies governed by a special Act. An OPC is separately entitled to
  // MGT-7A by Rule 11(1) regardless of size, which is why ENTITY_FORMS already
  // gives it mgt7a and why nothing here touches that.
  if (isPrivate) {
    const capitalBasis = 'Companies (Specification of Definitions Details) Amendment Rules, 2025 (effective 1 December 2025), Sec 2(85): paid-up capital not exceeding ₹10 crore AND turnover not exceeding ₹100 crore. The proviso also excludes holding and subsidiary companies, Sec 8 companies and companies governed by a special Act — none of which is on record here.';
    const figures = `Paid-up capital ${croreOrUnknown(paidUp)}; turnover ${croreOrUnknown(turnover)}.`;
    const overPaidUp = paidUp !== null && paidUp > SMALL_CO_PAID_UP_LIMIT;
    const overTurnover = turnover !== null && turnover > SMALL_CO_TURNOVER_LIMIT;

    if (overPaidUp || overTurnover) {
      const which = overPaidUp && overTurnover ? 'Both figures exceed'
        : overPaidUp ? 'Paid-up capital exceeds' : 'Turnover exceeds';
      hints.push({
        key: 'small_company',
        label: 'Small company — MGT-7A and abridged Board’s Report',
        basis: `${capitalBasis} ${figures} ${which} the limit, so the company is not small: MGT-7 (not MGT-7A) and a full Board’s Report apply.`,
        status: 'exempt_likely',
      });
    } else if (paidUp !== null && turnover !== null) {
      const small = determineSmallCompany(src, meta);
      hints.push({
        key: 'small_company',
        label: 'Small company — MGT-7A and abridged Board’s Report',
        basis: `${capitalBasis} ${figures} Both are within the limits, so the annual return is MGT-7A in place of MGT-7 (Rule 11(1)) and the Board’s Report may be prepared in abridged form under Rule 8A of the Companies (Accounts) Rules, 2014.${small.small === true && small.exclusionsUnknown ? ' The record does not say whether the company is a holding or subsidiary company or governed by a special Act; MGT-7A is shown on the assumption that it is none of these.' : ''}`,
        status: 'applies',
        // Consumed by attachObligationNotes → the annual-return row, which
        // computeFilingDeadlines has already switched to MGT-7A (a known-true
        // proviso exclusion would have made determineSmallCompany say no).
        formKey: small.small === true ? 'mgt7a' : 'mgt7',
        note: small.small === true
          ? `Small company under Sec 2(85) — annual return in MGT-7A in place of MGT-7.${small.exclusionsUnknown ? ' Assumes the company is not a holding or subsidiary company and not governed by a special Act; set these on the company record if otherwise.' : ''}`
          : 'Excluded from small-company status by the Sec 2(85) proviso — MGT-7 applies.',
      });
    } else {
      const missing = paidUp === null && turnover === null ? 'Neither figure is'
        : paidUp === null ? 'Paid-up capital is not' : 'Turnover is not';
      hints.push({
        key: 'small_company',
        label: 'Small company — MGT-7A and abridged Board’s Report',
        basis: `${capitalBasis} ${figures} ${missing} on record, so small-company status cannot be settled. ${UNKNOWN_FIGURE_CAVEAT}`,
        status: 'may_apply',
      });
    }
  }

  // ── CSR — Sec 135 ──────────────────────────────────────────
  //
  // An OR of three limbs, of which we can see exactly one. Turnover ≥ ₹1,000 cr
  // is therefore sufficient to say 'applies' but never sufficient to say no —
  // hence 'exempt_likely', which states that net worth and net profit were not
  // checked because they are not held.
  const csrBasis = 'Sec 135(1): a company with net worth of ₹500 crore or more, OR turnover of ₹1,000 crore or more, OR net profit of ₹5 crore or more during the immediately preceding financial year must constitute a CSR committee, adopt a policy, spend 2% of average net profits and file Form CSR-2. Net worth and net profit are not held on the company record, so only the turnover limb can be evaluated here.';
  if (turnover !== null && turnover >= CSR_TURNOVER_THRESHOLD) {
    hints.push({
      key: 'csr',
      label: 'CSR — committee, policy, 2% spend, Form CSR-2',
      basis: `${csrBasis} Turnover ${croreOrUnknown(turnover)} meets the ₹1,000 crore limb on its own.`,
      status: 'applies',
    });
  } else if (turnover !== null) {
    hints.push({
      key: 'csr',
      label: 'CSR — committee, policy, 2% spend, Form CSR-2',
      basis: `${csrBasis} Turnover ${croreOrUnknown(turnover)} is below ₹1,000 crore, but the net worth (₹500 crore) and net profit (₹5 crore) limbs are not on record and either could still trigger CSR.`,
      status: 'exempt_likely',
    });
  } else {
    hints.push({
      key: 'csr',
      label: 'CSR — committee, policy, 2% spend, Form CSR-2',
      basis: `${csrBasis} No turnover figure is on record, so no limb could be evaluated. ${UNKNOWN_FIGURE_CAVEAT}`,
      status: 'may_apply',
    });
  }

  // ── Secretarial audit (MR-3) — Sec 204 ─────────────────────
  //
  // Never 'exempt_likely'. The ₹100 crore borrowings limb reaches EVERY company
  // regardless of type or size, and borrowings are not held, so no company can
  // be ruled out from this record.
  const mr3Basis = 'Sec 204 read with Rule 9 of the Companies (Appointment and Remuneration of Managerial Personnel) Rules, 2014: every listed company, every public company with paid-up capital of ₹50 crore or more or turnover of ₹250 crore or more, and every company (of any type) with outstanding loans or borrowings from banks or public financial institutions of ₹100 crore or more must annex a secretarial audit report in Form MR-3. Borrowings are not held on the company record, so the ₹100 crore limb can never be ruled out here — this hint is therefore never reported as exempt.';
  const mr3PaidUpMet = isPublic && paidUp !== null && paidUp >= MR3_PAID_UP_THRESHOLD;
  const mr3TurnoverMet = isPublic && turnover !== null && turnover >= MR3_TURNOVER_THRESHOLD;
  if (listed || mr3PaidUpMet || mr3TurnoverMet) {
    const why = listed ? 'The company is listed'
      : mr3PaidUpMet ? `Public company with paid-up capital ${croreOrUnknown(paidUp)} (limb: ₹50 crore)`
        : `Public company with turnover ${croreOrUnknown(turnover)} (limb: ₹250 crore)`;
    hints.push({
      key: 'secretarial_audit',
      label: 'Secretarial audit — Form MR-3',
      basis: `${mr3Basis} ${why}, which satisfies the test on its own.`,
      status: 'applies',
    });
  } else {
    hints.push({
      key: 'secretarial_audit',
      label: 'Secretarial audit — Form MR-3',
      basis: `${mr3Basis} Paid-up capital ${croreOrUnknown(paidUp)}; turnover ${croreOrUnknown(turnover)}. No visible limb is met, but the borrowings limb is unverifiable. ${UNKNOWN_FIGURE_CAVEAT}`,
      status: 'may_apply',
    });
  }

  // ── Internal audit — Sec 138 ───────────────────────────────
  const iaBasis = 'Sec 138 read with Rule 13 of the Companies (Accounts) Rules, 2014: every listed company; every unlisted public company with paid-up capital of ₹50 crore or more, turnover of ₹200 crore or more, outstanding borrowings from banks or public financial institutions exceeding ₹100 crore, or outstanding deposits of ₹25 crore or more; and every private company with turnover of ₹200 crore or more or outstanding borrowings exceeding ₹100 crore, must appoint an internal auditor. Borrowings and deposits are not held on the company record.';
  const iaPaidUpMet = isPublic && paidUp !== null && paidUp >= IA_PAID_UP_THRESHOLD;
  const iaTurnoverMet = turnover !== null && turnover >= IA_TURNOVER_THRESHOLD;
  if (listed || iaPaidUpMet || iaTurnoverMet) {
    const why = listed ? 'The company is listed'
      : iaPaidUpMet ? `Public company with paid-up capital ${croreOrUnknown(paidUp)} (limb: ₹50 crore)`
        : `Turnover ${croreOrUnknown(turnover)} (limb: ₹200 crore)`;
    hints.push({
      key: 'internal_audit',
      label: 'Internal audit — appointment of an internal auditor',
      basis: `${iaBasis} ${why}, which satisfies the test on its own.`,
      status: 'applies',
    });
  } else {
    hints.push({
      key: 'internal_audit',
      label: 'Internal audit — appointment of an internal auditor',
      basis: `${iaBasis} Paid-up capital ${croreOrUnknown(paidUp)}; turnover ${croreOrUnknown(turnover)}. No visible limb is met, but the borrowings and deposits limbs are unverifiable. ${UNKNOWN_FIGURE_CAVEAT}`,
      status: 'may_apply',
    });
  }

  // ── Cost records / cost audit — Sec 148 ────────────────────
  //
  // Capped at 'may_apply' by construction. The rules are SECTOR-gated: a company
  // outside the Table A/B product-and-service classes has no obligation at any
  // turnover, and the record holds no sector, CETA heading or activity class. A
  // turnover of ₹5,000 crore therefore still cannot produce 'applies'.
  const costBasis = 'Sec 148 read with the Companies (Cost Records and Audit) Rules, 2014: maintenance of cost records applies only to companies engaged in the regulated (Table A) or non-regulated (Table B) products and services listed in Rule 3, with overall turnover of ₹35 crore or more in the preceding financial year; cost AUDIT applies above higher, table-specific limits. The company’s sector and product/service class are not held on the company record, so this can never rise above “may apply” here.';
  if (turnover !== null && turnover < COST_RECORDS_TURNOVER_THRESHOLD) {
    hints.push({
      key: 'cost_records',
      label: 'Cost records / cost audit — Sec 148',
      basis: `${costBasis} Turnover ${croreOrUnknown(turnover)} is below the ₹35 crore turnover gate, which rules out the turnover limb irrespective of sector.`,
      status: 'exempt_likely',
    });
  } else {
    hints.push({
      key: 'cost_records',
      label: 'Cost records / cost audit — Sec 148',
      basis: `${costBasis} Turnover ${croreOrUnknown(turnover)}. ${UNKNOWN_FIGURE_CAVEAT}`,
      status: 'may_apply',
    });
  }

  // ── CARO 2020 ──────────────────────────────────────────────
  //
  // Paragraph 1(2) of the Order lists two exclusions that matter here and are
  // easy to confuse: (iv) a SMALL company under Sec 2(85) — so the 2025 rise in
  // the small-company thresholds DID take more private companies out of CARO —
  // and (v) a private company meeting CARO's own three limbs (₹1 cr paid-up +
  // reserves / ₹1 cr bank borrowings / ₹10 cr revenue), which the 2025 rules
  // did not touch. Small-company status is decided from the record; the three
  // limbs need figures the record does not hold, and the auditor's report
  // studio asks for them.
  const caroBasis = 'CARO 2020, paragraph 1(2): the Order does not apply to a banking or insurance company, a Section 8 company, a One Person Company, a small company under Sec 2(85) (paid-up capital ≤ ₹10 crore and turnover ≤ ₹100 crore since 1 December 2025), or a private company that is neither a subsidiary nor a holding company of a public company whose paid-up capital plus reserves and surplus does not exceed ₹1 crore at the balance sheet date, whose total borrowings from any bank or financial institution did not exceed ₹1 crore at any point during the year, and whose total revenue did not exceed ₹10 crore. Reserves and surplus, borrowings and total revenue are not held on the company record.';
  const smallForCaro = determineSmallCompany(src, meta);
  if (smallForCaro.small === true) {
    hints.push({
      key: 'caro_2020',
      label: 'CARO 2020 — auditor’s report reporting requirements',
      basis: `${caroBasis} Paid-up capital ${croreOrUnknown(paidUp)} and turnover ${croreOrUnknown(turnover)} make this a small company under Sec 2(85), which paragraph 1(2)(iv) excludes: CARO 2020 does not apply, and the auditor’s report should say so instead of carrying Annexure A.`,
      status: 'exempt_likely',
    });
  } else if (isPrivate && paidUp !== null && paidUp <= CARO_PAID_UP_RESERVES_LIMIT) {
    hints.push({
      key: 'caro_2020',
      label: 'CARO 2020 — auditor’s report reporting requirements',
      basis: `${caroBasis} Paid-up capital ${croreOrUnknown(paidUp)} is within the ₹1 crore limb before adding reserves, so the carve-out may be available — the remaining limbs cannot be checked from this record.`,
      status: 'exempt_likely',
    });
  } else {
    hints.push({
      key: 'caro_2020',
      label: 'CARO 2020 — auditor’s report reporting requirements',
      basis: `${caroBasis} ${isPrivate ? `Paid-up capital ${croreOrUnknown(paidUp)}.` : 'The carve-out is available only to private companies.'} ${UNKNOWN_FIGURE_CAVEAT}`,
      status: 'may_apply',
    });
  }

  // ── Internal financial controls reporting ──────────────────
  const ifcBasis = 'Sec 143(3)(i): the auditor must report on the adequacy and operating effectiveness of internal financial controls. Notification G.S.R. 583(E) dated 13 June 2017 exempts a private company that is a one-person company or a small company, or whose turnover is less than ₹50 crore as per the latest audited financial statement, or whose aggregate borrowings from banks, financial institutions or any body corporate are less than ₹25 crore at any point during the financial year — and only if the company has filed its Sec 92 annual returns and Sec 137 financial statements. Borrowings are not held on the company record. These exemption limits are UNAFFECTED by the Companies (Specification of Definitions Details) Amendment Rules, 2025 revision of the small-company thresholds, except through the small-company limb itself.';
  if (!isPrivate && entity !== 'opc') {
    hints.push({
      key: 'ifc_reporting',
      label: 'Internal financial controls — auditor’s report',
      basis: `${ifcBasis} The exemption reaches private companies and one-person companies only, so it is not available to a ${ENTITY_LABELS[entity] || entity} company.`,
      status: 'applies',
    });
  } else if (turnover !== null && turnover < IFC_TURNOVER_LIMIT) {
    hints.push({
      key: 'ifc_reporting',
      label: 'Internal financial controls — auditor’s report',
      basis: `${ifcBasis} Turnover ${croreOrUnknown(turnover)} is under ₹50 crore, so the exemption may be available subject to the filing condition.`,
      status: 'exempt_likely',
    });
  } else {
    hints.push({
      key: 'ifc_reporting',
      label: 'Internal financial controls — auditor’s report',
      basis: `${ifcBasis} Turnover ${croreOrUnknown(turnover)}. ${UNKNOWN_FIGURE_CAVEAT}`,
      status: 'may_apply',
    });
  }

  return hints;
}

/**
 * Copy hint-derived notes onto the checklist rows they belong to, ADDITIVELY.
 *
 * Rows are returned as new objects with an `obligationNotes` array; nothing
 * existing is altered and no form is swapped. The MGT-7 → MGT-7A note is the
 * only user today: MGT-7A is an entitlement the filer exercises, and the Sec
 * 2(85) proviso exclusions (holding/subsidiary status) are not on record, so
 * asserting the substitution would be a guess dressed as a determination.
 */
function attachObligationNotes(deadlines, hints) {
  const byForm = new Map();
  for (const hint of hints || []) {
    if (!hint?.formKey || !hint.note || hint.status !== 'applies') continue;
    if (!byForm.has(hint.formKey)) byForm.set(hint.formKey, []);
    byForm.get(hint.formKey).push(hint.note);
  }
  if (!byForm.size) return Array.isArray(deadlines) ? deadlines : [];
  return (deadlines || []).map((item) => (
    byForm.has(item.key) ? { ...item, obligationNotes: byForm.get(item.key) } : item
  ));
}

// ─── Core Computation ────────────────────────────────────────

/**
 * Compute the latest permissible AGM date.
 * Standard: 6 months from FY end (Sec 96).
 * First AGM: 9 months if incorporated < 18 months before first FY end.
 */
function computeAgmDeadline(fyEndDate, incorporatedDate) {
  const fyEnd = toDate(fyEndDate);
  if (!fyEnd) return null;

  const inc = toDate(incorporatedDate);
  if (inc) {
    // Check if this is the first FY after incorporation
    const monthsSinceInc = (fyEnd.getFullYear() - inc.getFullYear()) * 12 + (fyEnd.getMonth() - inc.getMonth());
    if (monthsSinceInc >= 0 && monthsSinceInc < 18) {
      // First AGM — 9 months from FY end
      return addMonths(fyEnd, 9);
    }
  }

  // Standard: 6 months from FY end
  return addMonths(fyEnd, 6);
}

/**
 * Compute the status of a single deadline relative to today.
 */
function computeDeadlineStatus(deadlineDate, today) {
  const due = toDate(deadlineDate);
  const now = startOfDay(today) || startOfDay(new Date());
  if (!due) return { daysRemaining: null, status: 'unknown', label: 'Unknown' };

  const days = diffDays(due, now);

  if (days < 0) return { daysRemaining: days, status: 'overdue', label: `Overdue ${Math.abs(days)}d` };
  if (days <= 15) return { daysRemaining: days, status: 'due-soon', label: `Due in ${days}d` };
  return { daysRemaining: days, status: 'compliant', label: `${days}d remaining` };
}

/**
 * Estimate penalty for a given form and overdue days.
 *
 * opts (optional), consumed only by the slab-based forms and ignored elsewhere:
 *   { smallLlp, contributionInr }  — LLP forms ('llp2022')
 *   { authorisedCapital }          — Companies Act forms ('companies2018')
 * Defaults are conservative in both regimes: the non-small LLP rate with a ₹100
 * assumed normal fee, and the TOP company fee slab when capital is unknown.
 */
function estimatePenalty(formKey, daysOverdue, opts = {}) {
  const form = FORM_REGISTRY[formKey];
  if (!form || daysOverdue <= 0) return { perDay: 0, total: 0, max: null, note: '' };

  const { perDay = 0, max = null, flat = 0, note = '', slabs = null } = form.penalty;

  if (slabs === 'llp2022') {
    return { perDay: 0, total: estimateLlpSlabPenalty(daysOverdue, opts), max: null, note };
  }

  if (slabs === 'companies2018') {
    // `section` comes from the registry entry, not the caller: whether the 1×
    // ≤15-day carve-out applies is a property of the FORM, and letting a caller
    // pass it would make the same form cost different amounts by accident.
    return {
      perDay: 0,
      total: estimateCompanySlabPenalty(daysOverdue, { ...opts, section: form.section }),
      max: null,
      note,
    };
  }

  let total = flat;
  if (perDay > 0) {
    total += perDay * daysOverdue;
    if (max !== null && total > max) total = max;
  }

  // Special: AGM penalty (not in registry but can be computed)
  return { perDay, total, max, note };
}

/** AGM-specific penalty (Sec 96 non-compliance) */
// Dates from which each fee table applies. The Companies table is the one
// substituted by G.S.R. 435(E) (w.e.f. 7 May 2018) and re-substituted in the same
// shape from 1 Jul 2022; the LLP table is the LLP (Amendment) Rules 2022
// (w.e.f. 1 Apr 2022). Before these dates a different table applied and this one
// must not be quoted for them.
const FEE_TABLE_FROM = {
  companies2018: { date: new Date(Date.UTC(2018, 4, 7)), label: '7 May 2018', source: 'Companies (Registration Offices and Fees) Rules, 2014, Annexure (Table of Fees), as amended in 2018 and 2022' },
  llp2022: { date: new Date(Date.UTC(2022, 3, 1)), label: '1 April 2022', source: 'LLP Rules, 2009, Annexure A, as substituted by the LLP (Amendment) Rules, 2022' },
};

/**
 * The late-filing fee for a form, with its workings, so a person can check the sum.
 *
 * estimatePenalty returns one number. This returns the same number and how it was
 * reached: the normal filing fee and the slab it came from, the delay band, the
 * multiplier, and every assumption made (an unknown authorised capital takes the
 * top slab, an unknown LLP size takes the higher rate). The total is
 * estimatePenalty's own, so the two can never disagree.
 *
 * @param {string} formKey  a FORM_REGISTRY key
 * @param {number} daysOverdue
 * @param {object} [opts]   { authorisedCapital } | { smallLlp, contributionInr }
 * @returns {object|null}   null for an unknown form
 */
function explainFilingFee(formKey, daysOverdue, opts = {}) {
  const form = FORM_REGISTRY[formKey];
  if (!form) return null;
  const days = Math.max(0, parseInt(daysOverdue, 10) || 0);
  const { slabs = null, perDay = 0, max = null, flat = 0, note = '' } = form.penalty;
  const total = estimatePenalty(formKey, days, opts).total;
  const base = { form: form.code, name: form.name, section: form.section, daysOverdue: days, additionalFee: total, note };

  if (slabs === 'companies2018') {
    const capKnown = Number(opts.authorisedCapital) > 0;
    const fee = companyNormalFee(opts.authorisedCapital);
    const band = COMPANY_ADDITIONAL_FEE_BANDS.find((b) => days <= b.maxDays) || null;
    const carveOut = band && band.maxDays === 15 && !COMPANY_FIRST_BAND_SECTIONS.includes(String(form.section).trim());
    const multiplier = days <= 0 ? 0 : (carveOut ? COMPANY_ADDITIONAL_FEE_BANDS[1].multiplier : band.multiplier);
    const assumptions = [];
    if (!capKnown) assumptions.push(`Authorised capital not given, so the top fee slab (Rs. ${COMPANY_DEFAULT_NORMAL_FEE}) is used. A smaller company pays less.`);
    if (carveOut) assumptions.push('The 1x band for up to 15 days applies only to sections 139 and 157; this form starts at 2x.');
    return {
      ...base,
      regime: 'companies2018',
      normalFee: fee,
      normalFeeBasis: capKnown ? `authorised capital Rs. ${Number(opts.authorisedCapital).toLocaleString('en-IN')}` : 'authorised capital unknown',
      multiplier,
      delayBand: days <= 0 ? null : (band.maxDays === Infinity ? 'more than 180 days' : `up to ${band.maxDays} days`),
      workings: days <= 0 ? 'Filed on time: no additional fee.' : `${multiplier} x Rs. ${fee} (normal fee) = Rs. ${total}`,
      totalPayable: days <= 0 ? fee : fee + total,
      totalPayableNote: 'Normal filing fee plus the additional fee.',
      assumptions,
      table: FEE_TABLE_FROM.companies2018,
    };
  }

  if (slabs === 'llp2022') {
    const contribution = Number(opts.contributionInr);
    const fee = llpNormalFee(opts.contributionInr);
    const small = opts.smallLlp === true;
    const rate = small ? 'small' : 'other';
    const band = LLP_ADDITIONAL_FEE_BANDS.find((b) => days <= b.maxDays) || null;
    const assumptions = [];
    if (!(contribution > 0)) assumptions.push(`Contribution not given, so a normal fee of Rs. ${LLP_DEFAULT_NORMAL_FEE} (the Rs. 1 to 5 lakh slab) is assumed.`);
    if (!small) assumptions.push('Treated as not a small LLP (the higher rate). A small LLP (contribution up to Rs. 25 lakh and turnover up to Rs. 40 lakh) pays half the multiplier.');
    let workings = 'Filed on time: no additional fee.';
    let multiplier = 0;
    if (days > 0 && band) {
      multiplier = band[rate];
      workings = `${multiplier} x Rs. ${fee} (normal fee) = Rs. ${total}`;
    } else if (days > 0) {
      const beyond = LLP_BEYOND_360[rate];
      multiplier = beyond.multiplier;
      workings = `${beyond.multiplier} x Rs. ${fee} + Rs. ${beyond.perDay} x ${days - 360} days beyond the 360th = Rs. ${total}`;
    }
    return {
      ...base,
      regime: 'llp2022',
      normalFee: fee,
      normalFeeBasis: contribution > 0 ? `contribution Rs. ${contribution.toLocaleString('en-IN')}` : 'contribution unknown',
      multiplier,
      delayBand: days <= 0 ? null : (band ? `up to ${band.maxDays} days` : 'more than 360 days'),
      workings,
      totalPayable: days <= 0 ? fee : fee + total,
      totalPayableNote: 'Normal filing fee plus the additional fee.',
      assumptions,
      table: FEE_TABLE_FROM.llp2022,
    };
  }

  // Flat or per-day forms: the registry's own figure, shown plainly.
  const parts = [];
  if (flat) parts.push(`Rs. ${flat} flat`);
  if (perDay) parts.push(`Rs. ${perDay} x ${days} days`);
  return {
    ...base,
    regime: perDay || flat ? (perDay ? 'per_day' : 'flat') : 'none',
    perDay,
    cap: max,
    multiplier: null,
    workings: days <= 0 ? 'Filed on time: no additional fee.' : `${parts.join(' + ') || 'no fixed amount in the registry'}${max !== null && days > 0 ? ` (capped at Rs. ${max})` : ''} = Rs. ${total}`,
    totalPayable: null,
    assumptions: [],
    table: null,
  };
}

function estimateAgmPenalty(daysOverdue) {
  if (daysOverdue <= 0) return { total: 0, note: '' };
  return {
    total: 100000 + (daysOverdue * 5000),
    note: '\u20B91,00,000 + \u20B95,000/day',
  };
}

/**
 * Index a company's recorded filings.
 *
 * Without this the engine has no way to know a form was actually filed, so a
 * fully compliant company saw every past-dated form as overdue and carried a
 * fabricated penalty. Accepts either shape:
 *   filedForms: [{ form: 'aoc4' | 'AOC-4', fyEnd | fy | financialYear, filedOn }]
 *   filings:    same
 *
 * Records are indexed twice over: `key@YYYY` when the record names the FY it
 * belongs to, and bare `key` when it does not. That split is what lets the
 * dual-FY-cycle view mark last year's AOC-4 filed without also clearing this
 * year's \u2014 both rows carry the same form code and would otherwise collide.
 * A record with no FY still applies to every cycle, which is the forgiving
 * reading a user recording "AOC-4: filed" generally means.
 *
 * @returns {Map<string, Date>} 'key' | 'key@YYYY' -> date filed
 */
function buildFiledIndex(company, fyEndDate) {
  const out = new Map();
  const records = company?.filedForms || company?.filings;
  if (!Array.isArray(records) || !records.length) return out;

  const codeToKey = new Map(
    Object.entries(FORM_REGISTRY).map(([key, f]) => [f.code.toUpperCase(), key]),
  );

  for (const rec of records) {
    if (!rec) continue;
    const raw = String(rec.form || rec.formKey || rec.code || '').trim();
    if (!raw) continue;
    const key = FORM_REGISTRY[raw] ? raw : codeToKey.get(raw.toUpperCase());
    if (!key) continue;

    const recFy = toDate(rec.fyEnd || rec.fy || rec.financialYear);
    const filedOn = toDate(rec.filedOn || rec.filedDate || rec.date) || recFy || fyEndDate;
    if (!filedOn) continue;

    const mapKey = recFy ? `${key}@${recFy.getFullYear()}` : key;
    const prev = out.get(mapKey);
    if (!prev || filedOn < prev) out.set(mapKey, filedOn);
  }
  return out;
}

/**
 * Resolve a form to its filing date for one FY cycle: the FY-scoped record
 * wins, an unscoped record is the fallback.
 */
function lookupFiled(index, key, cycleFyEndDate) {
  if (!index || !index.size) return null;
  if (cycleFyEndDate) {
    const scoped = index.get(`${key}@${cycleFyEndDate.getFullYear()}`);
    if (scoped) return scoped;
  }
  return index.get(key) || null;
}

/**
 * Compute all filing deadlines for a given AGM date, entity type, and FY end.
 * This is the stateless calculator function (used by both the Calculator page
 * and the backend API).
 *
 * @param {string|Date} agmDate     — Date the AGM was held (or null if not held)
 * @param {string}      entityType  — normalised entity key
 * @param {string|Date} fyEnd       — FY end descriptor ("03-31", "31 March", or Date)
 * @param {Date}        [today]     — override "now" for testing
 * @param {string|Date} [incorporatedDate] — company incorporation date (for first AGM)
 * @param {object} [companyMeta] — { contributionInr, turnoverInr } for the LLP
 *   slab penalty (smallLlp is derived only when BOTH thresholds are positively
 *   met, otherwise the conservative non-small rate applies), plus
 *   { authorisedCapital } for the Companies Act slab penalty (unknown ⇒ the top
 *   fee slab, same conservative convention), plus
 *   { filings, mcaFilings, filingCycleKey } for filed tracking (a form recorded
 *   filed for the cycle reports status 'filed' and never accrues a penalty),
 *   plus { xbrl } — a determineXbrl() result which selects aoc4xbrl over aoc4.
 * @returns {{ deadlines: Array, agmDeadline: object, fyEndDate: Date, fyLabel: string }}
 */
function computeFilingDeadlines(agmDate, entityType, fyEnd, today, incorporatedDate, companyMeta) {
  const now = startOfDay(today) || startOfDay(new Date());
  const entity = normalizeEntityType(entityType);
  const contributionInr = Number(companyMeta?.contributionInr ?? companyMeta?.contribution) || null;
  const turnoverInr = Number(companyMeta?.turnoverInr ?? companyMeta?.turnover) || null;
  const penaltyOpts = {
    contributionInr,
    smallLlp: contributionInr !== null && turnoverInr !== null && contributionInr <= 2500000 && turnoverInr <= 4000000,
    // Nominal share capital, which sets the normal filing fee the Companies Act
    // slab multipliers are applied to. 0 / absent ⇒ null ⇒ the top fee slab.
    authorisedCapital: Number(companyMeta?.authorisedCapital) || null,
  };
  const fyEndDate = parseFyEnd(fyEnd, now);
  const agm = toDate(agmDate);

  // Filed tracking. The cycle key can be overridden so the dual-cycle view
  // attributes previous-FY rows to the FY they actually belong to. Manual
  // records are overlaid on registry-derived ones and always win.
  const filings = mergeFilings(
    normalizeFilings(companyMeta?.filings),
    normalizeFilings(companyMeta?.mcaFilings),
  );
  const cycleKey = companyMeta?.filingCycleKey || fyKey(fyEndDate);
  const cycleFyLabel = cycleKey ? `FY ${cycleKey}` : '';

  // AOC-4 vs AOC-4 XBRL is a determination, not a per-entity constant.
  const xbrl = companyMeta?.xbrl || null;

  const agmRequired = AGM_REQUIRED[entity] !== false;
  const agmDeadlineDate = agmRequired ? computeAgmDeadline(fyEndDate, incorporatedDate) : null;
  const agmHeld = agm !== null && agmRequired;

  // When AGM is required but not held, use the statutory AGM deadline as
  // the assumed AGM date for computing form deadlines (worst-case estimate).
  // This ensures dashboard shows meaningful data instead of all-zero.
  const agmNotSet = agmRequired && !agm;
  const effectiveAgm = agm || (agmRequired ? agmDeadlineDate : null);

  const baseFormKeys = ENTITY_FORMS[entity] || ENTITY_FORMS.pvtlimited;
  // Small company (Sec 2(85)) → MGT-7A in place of MGT-7, the same way XBRL
  // selects AOC-4 XBRL over AOC-4. Undetermined stays MGT-7.
  const smallCompany = companyMeta?.smallCompany || null;
  const formKeys = baseFormKeys
    .map((k) => (xbrl?.required === true && k === 'aoc4' ? 'aoc4xbrl' : k))
    .map((k) => (smallCompany?.small === true && k === 'mgt7' ? 'mgt7a' : k));

  const deadlines = formKeys.map((key) => {
    const form = FORM_REGISTRY[key];
    if (!form) return null;

    let dueDate = null;
    let estimated = false;

    // `now` is the third argument for the sake of the half-yearly forms, whose
    // due date is "the next one that has not passed" rather than a function of
    // the FY close. Every other computeDue ignores it.
    if (form.requiresAgm) {
      if (agm) {
        dueDate = form.computeDue(agm, fyEndDate, now);
      } else if (agmRequired && effectiveAgm) {
        // Estimate based on statutory AGM deadline
        dueDate = form.computeDue(effectiveAgm, fyEndDate, now);
        estimated = true;
      }
    } else {
      dueDate = form.computeDue(agm, fyEndDate, now);
    }

    // A form recorded as filed is settled: no overdue state, no penalty.
    // `filed: false` records are present but negative — test .filed, not presence.
    const itemFilingKey = filingKey(key, cycleKey);
    // MGT-7 and MGT-7A are one obligation (Sec 92) that can change its form
    // when a company's size changes: a flag recorded under either key settles
    // the row.
    const siblingKey = key === 'mgt7' ? 'mgt7a' : key === 'mgt7a' ? 'mgt7' : null;
    const filedEntry = filings[itemFilingKey] || (siblingKey ? filings[filingKey(siblingKey, cycleKey)] : null) || null;
    const isFiled = !!filedEntry?.filed;

    const statusInfo = isFiled
      ? { daysRemaining: diffDays(dueDate, now), status: 'filed', label: 'Filed' }
      : computeDeadlineStatus(dueDate, now);

    const penaltyInfo = statusInfo.status === 'overdue'
      ? estimatePenalty(key, Math.abs(statusInfo.daysRemaining), penaltyOpts)
      : { perDay: 0, total: 0, max: null, note: form.penalty.note };

    return {
      key,
      code: form.code,
      name: form.name,
      section: form.section,
      dueDate,
      dueDateFormatted: formatDateEN(dueDate),
      ...statusInfo,
      penalty: penaltyInfo,
      penaltyNote: form.penalty.note,
      requiresAgm: form.requiresAgm,
      pendingAgm: false,
      estimated,
      agmNotSet: estimated && agmNotSet,
      filed: isFiled,
      filedAt: filedEntry?.filedAt || null,
      filedSource: filedEntry ? filedEntry.source : null,
      filedAsOf: filedEntry?.asOf || null,
      filedBasis: filedBasisLabel(filedEntry),
      filedOverridesMca: !!filedEntry?.overridesMca,
      // Optional evidence captured with the manual flag (SRN / real filing date).
      filedSrn: filedEntry?.srn || '',
      filedOn: filedEntry?.filedOn || null,
      filedHasEvidence: hasFilingEvidence(filedEntry),
      filingKey: itemFilingKey,
      cycleFyLabel,
      colorClass: form.colorClass,
    };
  }).filter(Boolean);

  // AGM deadline entry (for entities that require AGM)
  let agmDeadline = null;
  if (agmRequired && agmDeadlineDate) {
    const agmStatus = agmHeld
      ? { daysRemaining: diffDays(agmDeadlineDate, now), status: 'compliant', label: 'AGM Held' }
      : computeDeadlineStatus(agmDeadlineDate, now);

    const agmPenalty = !agmHeld && agmStatus.status === 'overdue'
      ? estimateAgmPenalty(Math.abs(agmStatus.daysRemaining))
      : { total: 0, note: '\u20B91,00,000 + \u20B95,000/day' };

    agmDeadline = {
      key: 'agm',
      code: 'AGM',
      name: 'Annual General Meeting',
      section: 'Sec 96',
      dueDate: agmDeadlineDate,
      dueDateFormatted: formatDateEN(agmDeadlineDate),
      ...agmStatus,
      penalty: agmPenalty,
      penaltyNote: '\u20B91,00,000 + \u20B95,000/day',
      requiresAgm: false,
      pendingAgm: false,
      estimated: false,
      agmNotSet,
      // The AGM is tracked by its own `lastAgm` date, not the filings map.
      filed: false,
      filedAt: null,
      filedSource: null,
      filedAsOf: null,
      filedBasis: '',
      filedOverridesMca: false,
      filedSrn: '',
      filedOn: null,
      filedHasEvidence: false,
      filingKey: null,
      cycleFyLabel,
      colorClass: 'text-[#8a6a1f]',
      agmHeld,
    };
  }

  // Total penalty exposure
  const totalPenalty = deadlines.reduce((sum, d) => sum + (d.penalty?.total || 0), 0)
    + (agmDeadline?.penalty?.total || 0);

  // Next critical deadline
  const allItems = [...deadlines, ...(agmDeadline ? [agmDeadline] : [])].filter(
    (d) => d.dueDate
  );
  allItems.sort((a, b) => (a.dueDate?.getTime() || 0) - (b.dueDate?.getTime() || 0));
  const nextCritical = allItems.find((d) => d.status === 'overdue' || d.status === 'due-soon') || allItems[0] || null;

  return {
    deadlines,
    agmDeadline,
    fyEndDate,
    fyLabel: fyLabel(fyEndDate),
    entity,
    entityLabel: ENTITY_LABELS[entity] || entity,
    agmRequired,
    agmHeld,
    agmNotSet,
    totalPenalty,
    nextCritical,
    xbrl,
    smallCompany,
  };
}

// ─── Company-Level ───────────────────────────────────────────

/**
 * Merge deadlines from two computation cycles (previous-FY overdue + current-FY statutory).
 * Used when a company's lastAgm is from a previous FY — we need BOTH the real overdue
 * items AND the upcoming statutory deadlines for the current FY.
 */
function mergeDeadlineCycles(prevFyDeadlines, currentFyDeadlines) {
  const merged = [];
  // Previous FY: keep AGM-dependent forms that are still outstanding, plus the
  // ones the user marked filed so the checklist shows the cycle is settled
  // rather than silently dropping the row.
  for (const item of prevFyDeadlines) {
    if (item.requiresAgm && (item.status === 'overdue' || item.filed)) {
      merged.push({ ...item, previousFyCycle: true });
    }
  }
  // Current FY: AGM-dependent items (estimated upcoming) + non-AGM items (deduplicated)
  for (const item of currentFyDeadlines) {
    if (item.requiresAgm) {
      merged.push({ ...item, previousFyCycle: false, estimated: true, agmNotSet: true });
    } else {
      merged.push({ ...item, previousFyCycle: false });
    }
  }
  return merged;
}

/**
 * Compute all deadlines for a single company from its stored data.
 * Expects a normalised company object (from companyContract.js).
 *
 * Three cases:
 *   Case 1 — AGM held in current FY: single cycle with actual AGM date.
 *   Case 2 — AGM from previous FY:   dual-cycle (old overdue + current statutory).
 *   Case 3 — No AGM date at all:     single cycle with null (statutory fallback).
 */
function computeCompanyDeadlines(company, today) {
  if (!company) return null;
  const now = startOfDay(today) || startOfDay(new Date());
  const entity = normalizeEntityType(company.type);
  const fyEndDate = parseFyEnd(company.financialYearEnd, now);

  const lastAgm = toDate(company.lastAgm);
  const hasLastAgm = lastAgm !== null;
  const agmRequired = AGM_REQUIRED[entity] !== false;
  const currentFyAgmHeld = !!(lastAgm && fyEndDate && lastAgm >= fyEndDate);

  const companyInfo = {
    _id: company._id || company.id,
    cin: company.cin,
    name: company.name,
    type: company.type,
    entity,
  };

  // AOC-4 vs AOC-4 XBRL — one determination for the whole company, resolved
  // once here and reused by every cycle.
  const xbrl = determineXbrl(company);
  const smallCompany = determineSmallCompany(company);
  const mcaFilings = deriveRegistryFilings(company);

  // Penalty + filed-tracking metadata, rebuilt per cycle so a previous-FY row is
  // matched against that FY's filed flags rather than the current one's.
  const cycleMeta = (filingCycleKey) => ({
    contributionInr: company.contributionInr ?? company.contribution,
    turnoverInr: company.turnoverInr ?? company.turnover,
    authorisedCapital: company.authorisedCapital,
    filings: company.filings,
    mcaFilings,
    filingCycleKey,
    xbrl,
    smallCompany,
  });

  // ── Case 1: AGM held in current FY — standard single cycle ──
  if (currentFyAgmHeld) {
    const result = computeFilingDeadlines(
      lastAgm, entity, company.financialYearEnd, now, company.incorporated,
      cycleMeta(fyKey(fyEndDate)),
    );
    return {
      company: companyInfo,
      ...result,
      agmNotSet: false,
      agmNotSetForCurrentFy: false,
      hasLastAgm: true,
    };
  }

  // ── Case 2: AGM from previous FY — dual-cycle ──
  if (hasLastAgm && !currentFyAgmHeld && agmRequired) {
    // Cycle A: compute from actual lastAgm → detects overdue items from old FY.
    // Its filed flags live under the FY that lastAgm closed, not the current one.
    const prevCycleKey = fyKey(parseFyEnd(company.financialYearEnd, lastAgm));
    const prevFyResult = computeFilingDeadlines(
      lastAgm, entity, company.financialYearEnd, now, company.incorporated,
      cycleMeta(prevCycleKey),
    );
    // Cycle B: compute with null AGM → statutory fallback for current FY upcoming
    const currentFyResult = computeFilingDeadlines(
      null, entity, company.financialYearEnd, now, company.incorporated,
      cycleMeta(fyKey(fyEndDate)),
    );

    // Merge: old-FY overdue + current-FY estimated upcoming
    const mergedDeadlines = mergeDeadlineCycles(prevFyResult.deadlines, currentFyResult.deadlines);

    // Use current FY's AGM deadline (statutory) for the AGM entry
    const agmDeadline = currentFyResult.agmDeadline;

    // Recalculate aggregates from merged set
    const allItems = [...mergedDeadlines, ...(agmDeadline ? [agmDeadline] : [])].filter(d => d.dueDate);
    allItems.sort((a, b) => (a.dueDate?.getTime() || 0) - (b.dueDate?.getTime() || 0));
    const nextCritical = allItems.find(d => d.status === 'overdue' || d.status === 'due-soon') || allItems[0] || null;
    const totalPenalty = mergedDeadlines.reduce((s, d) => s + (d.penalty?.total || 0), 0)
      + (agmDeadline?.penalty?.total || 0);

    return {
      company: companyInfo,
      deadlines: mergedDeadlines,
      agmDeadline,
      fyEndDate: currentFyResult.fyEndDate,
      fyLabel: currentFyResult.fyLabel,
      entity,
      entityLabel: currentFyResult.entityLabel,
      agmRequired: currentFyResult.agmRequired,
      agmHeld: false,
      agmNotSet: false,
      agmNotSetForCurrentFy: true,
      hasLastAgm: true,
      totalPenalty,
      nextCritical,
      xbrl,
      smallCompany,
    };
  }

  // ── Case 3: No AGM date at all — single cycle with statutory fallback ──
  const result = computeFilingDeadlines(
    null, entity, company.financialYearEnd, now, company.incorporated,
    cycleMeta(fyKey(fyEndDate)),
  );
  return {
    company: companyInfo,
    ...result,
    agmNotSet: result.agmRequired,
    agmNotSetForCurrentFy: false,
    hasLastAgm: false,
  };
}

// ─── Portfolio-Level ─────────────────────────────────────────

/**
 * Compute deadlines across all companies, returning aggregated stats,
 * alert list, and calendar events.
 */
function computePortfolioDeadlines(companies, today) {
  if (!Array.isArray(companies) || !companies.length) {
    return { results: [], alerts: [], stats: { total: 0, overdue: 0, dueSoon: 0, dueThisMonth: 0, compliant: 0, pending: 0, pendingSetup: 0 }, calendarEvents: [] };
  }

  const now = startOfDay(today) || startOfDay(new Date());
  const results = [];
  const alerts = [];
  const calendarEvents = [];
  let overdue = 0, dueSoon = 0, compliant = 0, pending = 0;
  let dueThisMonth = 0, pendingSetup = 0;

  for (const company of companies) {
    // Skip inactive companies (struck-off, dissolved, etc.)
    const status = String(company.status || '').toLowerCase();
    if (status.includes('struck off') || status.includes('dissolved') ||
        status.includes('liquidated') || status.includes('defunct')) continue;

    const compResult = computeCompanyDeadlines(company, now);
    if (!compResult) continue;

    results.push(compResult);

    // Classify the company's overall status
    const allItems = [...compResult.deadlines, ...(compResult.agmDeadline ? [compResult.agmDeadline] : [])];
    const hasOverdue = allItems.some((d) => d.status === 'overdue');
    const hasDueSoon = allItems.some((d) => d.status === 'due-soon');
    const hasDueIn30 = allItems.some((d) => !d.filed && d.dueDate && d.daysRemaining !== null && d.daysRemaining >= 0 && d.daysRemaining <= 30);

    if (hasOverdue) overdue++;
    else if (hasDueSoon) dueSoon++;
    else compliant++;

    // Count companies with at least one filing due this month (within 30 days)
    if (hasDueIn30 && !hasOverdue) dueThisMonth++;

    // Count companies needing AGM date setup
    if (compResult.agmNotSet) pendingSetup++;

    for (const item of allItems) {
      if (item.status === 'overdue' || item.status === 'due-soon') {
        alerts.push({
          id: `${company.cin || company.name}-${item.key}`,
          companyName: company.name,
          companyCin: company.cin,
          companyType: company.type,
          companyCity: company.city || '',
          companyState: company.state || '',
          formCode: item.code,
          item: item.name,
          section: item.section,
          dueDate: item.dueDateFormatted,
          dueDateRaw: item.dueDate,
          daysRemaining: item.daysRemaining,
          status: item.status,
          priority: item.status === 'overdue' ? 'critical' : 'warning',
          penalty: item.penalty?.total || 0,
          penaltyPerDay: item.penalty?.perDay || 0,
          penaltyNote: item.penaltyNote,
          estimated: item.estimated || false,
          agmNotSet: item.agmNotSet || false,
          agmNotSetForCurrentFy: compResult.agmNotSetForCurrentFy || false,
        });
      }

      // Calendar events for all items with due dates
      if (item.dueDate) {
        calendarEvents.push({
          date: item.dueDate,
          month: item.dueDate.getMonth(),
          day: item.dueDate.getDate(),
          year: item.dueDate.getFullYear(),
          companyName: company.name,
          companyCin: company.cin,
          formCode: item.code,
          formName: item.name,
          status: item.status,
          tone: item.estimated
            ? (item.status === 'overdue' ? 'crit' : 'est')
            : (item.status === 'overdue' ? 'crit' : item.status === 'due-soon' ? 'warn' : 'info'),
          daysRemaining: item.daysRemaining,
          penalty: item.penalty?.total || 0,
          estimated: item.estimated || false,
          filed: item.filed || false,
          previousFyCycle: item.previousFyCycle || false,
        });
      }
    }
  }

  // Sort alerts: overdue first (most overdue at top), then due-soon (least days first)
  alerts.sort((a, b) => {
    if (a.status === 'overdue' && b.status !== 'overdue') return -1;
    if (a.status !== 'overdue' && b.status === 'overdue') return 1;
    return (a.daysRemaining ?? 0) - (b.daysRemaining ?? 0);
  });

  return {
    results,
    alerts,
    stats: {
      total: results.length,
      overdue,
      dueSoon,
      dueThisMonth,
      compliant,
      pending,
      pendingSetup,
    },
    calendarEvents,
  };
}

// ─── Utility Exports ─────────────────────────────────────────

// company (optional) resolves the AOC-4 / AOC-4 XBRL choice; without it the
// caller gets plain AOC-4, matching the conservative default in the engine.
function getEntityForms(entityType, company) {
  const entity = normalizeEntityType(entityType);
  const keys = ENTITY_FORMS[entity] || ENTITY_FORMS.pvtlimited;
  return keys
    .map((k) => resolveAoc4Key(k, company))
    .filter((k) => !FORM_REGISTRY[k]?.eventBased)
    .map((k) => ({ key: k, ...FORM_REGISTRY[k] }))
    .filter(Boolean);
}

function isAgmRequired(entityType) {
  const entity = normalizeEntityType(entityType);
  return AGM_REQUIRED[entity] !== false;
}

// ─── Exports ─────────────────────────────────────────────────

export {
  // Date helpers
  toDate,
  addDays,
  addMonths,
  formatDateEN,
  diffDays,

  // Normalizers
  normalizeEntityType,
  parseFyEnd,
  fyLabel,
  fyKey,
  filingKey,
  filingFormKey,
  normalizeFilings,
  normalizeFilingSource,
  isRegistryFilingSource,
  mergeFilings,
  deriveRegistryFilings,
  filedBasisLabel,
  hasFilingEvidence,

  // Registry & constants
  REGISTRY_AS_OF,
  REGISTRY_FILING_SOURCES,
  FORM_REGISTRY,
  ENTITY_FORMS,
  EVENT_BASED_FORMS,
  XBRL_HINT,
  AGM_REQUIRED,
  ENTITY_LABELS,
  FILING_KEY_ALIAS,

  // AOC-4 XBRL applicability
  XBRL_PAID_UP_THRESHOLD,
  XBRL_TURNOVER_THRESHOLD,
  parseInrAmount,
  parsePositiveInr,
  cinListingClass,
  determineXbrl,
  determineSmallCompany,

  // Half-yearly cadence
  computeMsme1NextDue,

  // Threshold-driven obligation hints
  computeObligationHints,
  attachObligationNotes,
  SMALL_CO_PAID_UP_LIMIT,
  SMALL_CO_TURNOVER_LIMIT,
  CSR_NET_WORTH_THRESHOLD,
  CSR_TURNOVER_THRESHOLD,
  CSR_NET_PROFIT_THRESHOLD,
  MR3_PAID_UP_THRESHOLD,
  MR3_TURNOVER_THRESHOLD,
  MR3_BORROWINGS_THRESHOLD,
  IA_PAID_UP_THRESHOLD,
  IA_TURNOVER_THRESHOLD,
  IA_BORROWINGS_THRESHOLD,
  IA_DEPOSITS_THRESHOLD,
  COST_RECORDS_TURNOVER_THRESHOLD,
  CARO_PAID_UP_RESERVES_LIMIT,
  CARO_BORROWINGS_LIMIT,
  CARO_REVENUE_LIMIT,
  IFC_TURNOVER_LIMIT,
  IFC_BORROWINGS_LIMIT,

  // Core computation
  computeAgmDeadline,
  computeDeadlineStatus,
  estimatePenalty,
  explainFilingFee,
  FEE_TABLE_FROM,
  estimateAgmPenalty,
  computeFilingDeadlines,
  llpNormalFee,
  estimateLlpSlabPenalty,
  companyNormalFee,
  estimateCompanySlabPenalty,

  // Company & portfolio
  computeCompanyDeadlines,
  computePortfolioDeadlines,

  // Utilities
  getEntityForms,
  isAgmRequired,
  requiresXbrl,
  buildFiledIndex,
  lookupFiled,
};
